/**
 * `Mandates` — audit-record list view (ADR-0003).
 *
 * Phase 2 (2026-05-31):
 *   - Surfaces both Discovery (`decision === 'observed'`) and Transactional
 *     records. The header pill summarizes the visible mix; each row's pill
 *     is colored by the decision class.
 *   - The list polls every 10s for newly-arrived records (top of the list)
 *     and infinite-scrolls forward through the cursor-paginated history at
 *     the bottom. The cursor pages do NOT re-poll — only the first page is
 *     refreshed.
 *   - The detail panel renders different shapes per chain class; see
 *     `MandateDetail.tsx`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight, Search, ShieldAlert, ShieldCheck, X } from 'lucide-react';

import type { AuditConnectorCall, AuditRecord, AuditRecordId, AuditRecordPayload, BotSummary } from '@sill/types';
import type { AuditQuery, AuditStatusFilter, VisitGroup, VisitSegmentFilter } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { ApiError, SUPPORT_READ_ONLY_REASON } from '../../api/http-client.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { getBlocking } from '../../api/threats.js';
import { useSites } from '../../app/providers.js';
import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { todayInZone, INBOUND_PROTOCOLS, INBOUND_PROTOCOL_LABEL } from '@sill/types';
import type { SetView, View } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';
import { isLiveSite, siteMode, siteStaleSignal } from '../../lib/site-state.js';
import { useIsBelowLg } from '../../lib/use-is-mobile.js';
import { Card, EmptyStateCard, NumLabel, Tooltip, type PillTone } from '../../components/atoms/index.js';
import { MandateDetailV3 } from './MandateDetailV3.js';
import { groupWindowLabel } from './visit-identity.js';
import { VisitGroupActivity } from './VisitGroupActivity.js';
import { VisitGroupRow, visitGroupName } from './VisitGroupRow.js';
import { PlanUsageBanner } from '../billing/PlanUsageBanner.js';
import { UcpSessionsList } from './UcpSessionsList.js';
import { UcpSessionDetail } from './UcpSessionDetail.js';
import { PersonRow } from './PersonRow.js';
import { PersonTimeline } from '../people/PersonTimeline.js';
import { listPeople } from '../../api/people.js';
import { listThreatCampaigns, listThreatSenders } from '../../api/threats.js';
import type { PersonSummary, ThreatCampaignSummary, ThreatSenderSummary } from '@sill/types/api';
import { CampaignCard } from './CampaignCard.js';
import { CampaignDetail } from './CampaignDetail.js';
import { SenderCard } from './SenderCard.js';
import { SenderDetail } from './SenderDetail.js';
import { VisitRecordRow } from './VisitRecordRow.js';
import { VisitTrustProvider, type VisitTrustValue } from './VisitTrustChip.js';
import { useCanDisputeTrust } from '../agents/trust/useCanDisputeTrust.js';
import { useTrustVerdicts } from '../agents/trust/useTrustVerdicts.js';
import type { MandateRecord } from './types.js';


/**
 * Visits panel Step B (labels-v2): the list shows one row per agent by
 * default; "Audit log view" (under More filters) restores the per-record (Step A) list for
 * audits and export. Persisted in the URL as `?list=visits` (`?view=` is the page router).
 */
export type ListView = 'agents' | 'visits';

/** ADR-0246 — the Audit log view toggle's tooltip (under More filters). */
export const AUDIT_LOG_VIEW_TOOLTIP = 'Every signed log entry, one per visitor per hour — for audits and exports.';
const LIST_VIEW_PARAM = 'list';

function readListView(): ListView {
  if (typeof window === 'undefined') return 'agents';
  try {
    return new URL(window.location.href).searchParams.get(LIST_VIEW_PARAM) === 'visits' ? 'visits' : 'agents';
  } catch {
    return 'agents';
  }
}

/**
 * `?date_from=` / `?date_to=` deep link — a YYYY-MM-DD day in the display
 * zone, mirroring the server's `DATE_RE`. Anything else (an ISO instant, a
 * relative hint, free text) is ignored so a stale link can't force an
 * invalid state into the date inputs' `max`/`min` arithmetic. Returns `''`
 * for the "unset" state (same wire shape the inputs use).
 */
const DATE_PARAM_RE = /^\d{4}-\d{2}-\d{2}$/;
export function readDateParam(name: 'date_from' | 'date_to'): string {
  if (typeof window === 'undefined') return '';
  try {
    const v = new URL(window.location.href).searchParams.get(name);
    return v !== null && DATE_PARAM_RE.test(v) ? v : '';
  } catch {
    return '';
  }
}

function writeListView(v: ListView): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (v === 'visits') url.searchParams.set(LIST_VIEW_PARAM, 'visits');
    else url.searchParams.delete(LIST_VIEW_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    /* URL persistence is enhancement only */
  }
}

function toneFg(C: ReturnType<typeof useC>, tone: PillTone): string {
  switch (tone) {
    case 'lime': return C.limeFg;
    case 'amber': return C.amberFg;
    case 'coral': return C.coralFg;
    case 'sky': return C.skyFg;
    case 'mint': return C.mintFg;
    case 'neutral':
    default:
      return C.text;
  }
}

function toneBg(C: ReturnType<typeof useC>, tone: PillTone): string {
  switch (tone) {
    case 'lime': return C.limeDim;
    case 'amber': return C.amberDim;
    case 'coral': return C.coralDim;
    case 'sky': return C.skyDim;
    case 'mint': return C.mintDim;
    case 'neutral':
    default:
      return 'transparent';
  }
}

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/**
 * Identity filter (labels-v2 Step A), sent as `?identity=`. Step C moved it
 * under "More filters": All · Confirmed · Mismatch. There is deliberately
 * no option for the default (unchecked) state. People are a chip now.
 */
export type IdentitySegment = 'all' | 'confirmed';
export const IDENTITY_SEGMENTS: { id: IdentitySegment; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'confirmed', label: 'Confirmed only' },
];

/**
 * Visits panel Step C (labels-v2 PROPOSAL-C.md, founder-approved
 * 2026-10-13): the list's chips, sent as `?segment=`. "All" is every agent
 * and bot; people are not mixed in, they have their own chip (founder:
 * "they can be a filter"). Each chip filters on the server, so pages and
 * totals stay true.
 */
export const SEGMENT_CHIPS: { id: VisitSegmentFilter; label: string }[] = [
  { id: 'bots', label: 'All bots' },
  { id: 'ai', label: 'AI agents' },
  { id: 'search', label: 'Search engines' },
  { id: 'seo', label: 'SEO & tools' },
  { id: 'suspicious', label: 'Suspicious' },
  // `orders` on the wire: every signed-mandate record (orders, refunds,
  // settlements, rejections, escalations). Hidden when there are none.
  { id: 'orders', label: 'Transactions' },
  { id: 'people', label: 'People' },
];

/** `?person=` → an opened visitor (ULID-shaped), else null. */
export function readPersonParam(href: string): string | null {
  try {
    const v = new URL(href).searchParams.get('person');
    return v !== null && /^[0-9A-Z]{26}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

/** `?segment=` / legacy `?identity=people` deep link → chip. */
export function readSegmentParam(href: string): VisitSegmentFilter {
  try {
    const u = new URL(href);
    const seg = u.searchParams.get('segment');
    if (SEGMENT_CHIPS.some((c) => c.id === seg)) return seg as VisitSegmentFilter;
    if (u.searchParams.get('identity') === 'people') return 'people';
    // Mismatch is part of Suspicious now (filters cleanup 2026-10-15).
    if (u.searchParams.get('identity') === 'mismatch') return 'suspicious';
    // `?threat=` is a Suspicious-only deep link; it implies the chip
    // (ADR-0245 + founder feedback 2026-10-17 — threat alert emails
    // link to the open alert).
    if (readThreatParam(href) !== null) return 'suspicious';
    // An outcome status link opens Transactions (the pills live there).
    if (readStatusParam(href) !== 'all') return 'orders';
  } catch {
    /* fall through */
  }
  return 'bots';
}

/**
 * `?threat=<threat_alert_id>` deep link — the threat alert email
 * (`services/api/src/email/templates/threat-alert.ts`) and the
 * Slack digest both write this when they link to a specific alert.
 * Validated as a bounded `tht_<26 alphanumeric>` shape so a stale /
 * malformed param never leaks into DOM queries; the exact match
 * against the loaded threats list below is the real correctness
 * gate (same posture as the record anchor's `anchorRecordRef`).
 */
const THREAT_ALERT_ID_RE = /^tht_[A-Za-z0-9]{26}$/;
export function readThreatParam(href: string): string | null {
  try {
    const v = new URL(href).searchParams.get('threat');
    return v !== null && THREAT_ALERT_ID_RE.test(v) ? v : null;
  } catch {
    return null;
  }
}

const STATUS_FILTERS: { id: AuditStatusFilter; label: string; tone: PillTone }[] = [
  { id: 'all', label: 'All', tone: 'neutral' },
  { id: 'approved', label: 'Approved', tone: 'lime' },
  { id: 'rejected', label: 'Rejected', tone: 'coral' },
  { id: 'escalated', label: 'Escalated', tone: 'amber' },
];

/** Old `?status=` links: an outcome opens Transactions; `observed` is gone (= All bots). */
export function readStatusParam(href: string): AuditStatusFilter {
  try {
    const v = new URL(href).searchParams.get('status');
    if (v === 'approved' || v === 'rejected' || v === 'escalated') return v;
  } catch {
    /* fall through */
  }
  return 'all';
}

/**
 * Mandates view polling cadence. The list refreshes every 10s while
 * the tab is visible so newly-signed audit records appear without a
 * manual reload. Polling pauses when the tab is hidden
 * (`document.hidden`) so a background tab doesn't burn API budget
 * for nothing. 10s is well above the consumer's typical
 * draft-to-persisted latency (~300ms incl. KMS sign) and well below
 * the average merchant's "hmm, is anything happening?" attention
 * threshold.
 */
const MANDATES_POLL_INTERVAL_MS = 10_000;
/**
 * The grouped list polls slower (security review Step B M1): each poll is a
 * GROUP BY over the whole range, and an agent row changes slowly. Paused
 * while the tab is hidden, like the per-record list.
 */
export const GROUPS_POLL_INTERVAL_MS = 30_000;
/** How long a freshly-arrived record stays flagged with a NEW pill. */
const NEW_RECORD_BADGE_MS = 6_000;
/** Server page size for `GET /v1/audit` (mirrors AUDIT_LIST_PAGE_SIZE).
 *  Used only for the "showing X–Y of N" range math; the server is the
 *  source of truth for the actual slice. */
const MANDATES_PAGE_SIZE = 50;

export interface MandatesProps {
  /** Routes the stale empty-state diagnostic to Settings, where the
   *  snippet-aware rotate-key flow lives, and the "Attack traffic is
   *  in Security →" link into the Security · Attackers tab. */
  setView: SetView;
}

export function Mandates({ setView }: MandatesProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const http = useOptionalHttp();
  const { activeSite } = useSites();
  // ADR-0235 — trust verdicts, the Agents page's hook and window. `null`
  // unless the site's verdicts are live: every chip then renders nothing.
  const trust = useTrustVerdicts(activeSite?.site_id ?? null, '30d');
  const canDisputeTrust = useCanDisputeTrust();
  const trustAgents = trust?.agents ?? null;
  const trustAddDispute = trust?.addDispute ?? null;
  const trustSiteId = activeSite?.site_id ?? null;
  const trustValue = useMemo<VisitTrustValue | null>(() => (
    trustAgents !== null && trustAddDispute !== null && trustSiteId !== null
      ? { verdicts: trustAgents, site_id: trustSiteId, canDispute: canDisputeTrust, windowPhrase: 'last 30 days', onDisputed: trustAddDispute }
      : null
  ), [trustAgents, trustAddDispute, trustSiteId, canDisputeTrust]);
  // ADR-0034 — state stores the wire-shape extension (`MandateRecord`)
  // which adds optional `visit_count` + `last_visit_at`. The signed
  // `AuditRecord` interface stays untouched in `packages/types`; the
  // sibling-table fields are DB-only metadata surfaced on the wire.
  const [records, setRecords] = useState<MandateRecord[]>([]);
  // Founder 2026-10-20: seed `loading` to `true` so the empty-state
  // ("No records") never flashes between mount and the first fetch
  // settling. The useEffect below flips it on inside its own closure
  // for every subsequent page / filter change.
  const [loading, setLoading] = useState(true);
  // Numbered (offset) pagination — replaces the prior cursor +
  // infinite-scroll model. `page` is 1-indexed; the server returns the
  // page slice plus `total_count` / `total_pages` so the pager can render
  // "page X of Y" and jump-to-page.
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Per-agent visit + action counts fetched in a single batch after each
  // page load. Key: agent_id. Absent entries mean the stats haven't loaded yet.
  const [agentStats, setAgentStats] = useState<Map<string, { visit_count: number; action_count: number }>>(
    () => new Map(),
  );
  // Deep-link `?q=` seed (Dashboard's Revenue-by-agent rows write this
  // when routing here — a per-agent aggregate has no single record_id
  // to deep-link to, so we pre-fill the search box with the agent
  // display name). Read once at mount; cleared from the URL after
  // consumption (one-shot, mirroring `?escalation=` on Resolution).
  const [query, setQuery] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    try {
      const v = new URL(window.location.href).searchParams.get('q');
      return v !== null ? v : '';
    } catch {
      return '';
    }
  });
  const [searchFocused, setSearchFocused] = useState(false);
  // ADR-0160 — the date range is calendar days in the display zone.
  const displayTz = useDisplayTimeZone();
  // Identity segment. Seeded once from `?identity=` so a deep link can open
  // a segment; unknown values fall back to All.
  const [filter, setFilter] = useState<IdentitySegment>(() => {
    if (typeof window === 'undefined') return 'all';
    try {
      const v = new URL(window.location.href).searchParams.get('identity');
      return IDENTITY_SEGMENTS.some((f) => f.id === v) ? (v as IdentitySegment) : 'all';
    } catch {
      return 'all';
    }
  });
  const [segment, setSegment] = useState<VisitSegmentFilter>(() =>
    (typeof window === 'undefined' ? 'bots' : readSegmentParam(window.location.href)));
  // Rows per chip from the grouped endpoint (Suspicious shows its count).
  const [segmentCounts, setSegmentCounts] = useState<Partial<Record<VisitSegmentFilter, number>> | null>(null);
  // Status (outcome: approved | rejected | escalated) only makes sense under
  // the Transactions chip — other chips are `observed` traffic only, and the
  // server combines segment + status as AND. A deep link like
  // `?status=rejected&segment=ai` previously inited to segment='orders' via
  // `readSegmentParam` (which upgrades any outcome link to Transactions);
  // but a direct URL that lands on a non-orders segment with a non-'all'
  // status would ask the server for `decision = 'rejected' AND
  // decision = 'observed'`, which is always empty. Coerce status to 'all'
  // whenever the initial segment is not 'orders', so the filter row stays
  // internally consistent regardless of how the view was reached.
  const [status, setStatus] = useState<AuditStatusFilter>(() => {
    if (typeof window === 'undefined') return 'all';
    const href = window.location.href;
    const seg = readSegmentParam(href);
    const st = readStatusParam(href);
    return seg === 'orders' ? st : 'all';
  });
  // ADR-0253 §A — protocol chip. Seeded from `?protocol=`; unknown values
  // fall back to All. The Overview "How agents reach you" card links here
  // with `?protocol=mcp` / `?protocol=a2a` / etc.
  const [protocol, setProtocol] = useState<string>(() => {
    if (typeof window === 'undefined') return 'all';
    try {
      const v = new URL(window.location.href).searchParams.get('protocol');
      if (v === null || v === 'all') return 'all';
      return (INBOUND_PROTOCOLS as readonly string[]).includes(v) ? v : 'all';
    } catch {
      return 'all';
    }
  });
  // Date range — seeded from `?date_from=` / `?date_to=` so a deep link
  // (threat alert emails, dashboard "last 7 days" tiles, shared links) can
  // reopen the exact range the merchant was reading. Only strict YYYY-MM-DD
  // is accepted; anything else is ignored (same `DATE_RE` the server uses).
  const [dateFrom, setDateFrom] = useState(() => readDateParam('date_from'));
  const [dateTo, setDateTo] = useState(() => readDateParam('date_to'));
  const [selected, setSelected] = useState<MandateRecord | null>(null);
  // Visits panel Step B — one row per agent (default) vs every visit.
  const [listView, setListView] = useState<ListView>(readListView);
  // No grouped endpoint (an API that predates it, or a test double without
  // it): fall back to the per-record list and hide the toggle.
  const [groupsUnavailable, setGroupsUnavailable] = useState<boolean>(
    () => api.mandates.listAgentGroups === undefined,
  );
  const effectiveView: ListView = groupsUnavailable ? 'visits' : listView;
  const [groups, setGroups] = useState<VisitGroup[]>([]);
  const [groupTotals, setGroupTotals] = useState<{ visits: number; records: number } | null>(null);
  // The grouped row open in the detail panel (`selected` is its newest
  // record). `null` while a single record is open: a one-record row, a
  // record opened from a group's Activity tab, or a deep link.
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  // ADR-0243 — People chip: one card per tracked visitor (GET /people),
  // and the open person's timeline. `null` = not loaded / not the chip.
  const [people, setPeople] = useState<readonly PersonSummary[] | null>(null);
  // People cards page with the list's pager (50 per page, like the route).
  const [peoplePaging, setPeoplePaging] = useState<{ total: number; pages: number }>({ total: 0, pages: 0 });
  const [peopleReload, setPeopleReload] = useState(0);
  // A Sill support session may not read the People list (visitors'
  // personal data, ADR-0216). Say so instead of showing an empty list.
  const [peopleHiddenForSupport, setPeopleHiddenForSupport] = useState(false);
  // ADR-0245 §4.1 — Suspicious → threats. Loaded only when the chip
  // Suspicious v2 (2026-10-17) — grouped-per-sender rows via the new
  // /threat-senders endpoint. The `?threat=tht_X` deep link opens the
  // sender group whose newest alert id is X (the API groups across
  // alerts; the deep-link resolver below passes the id through as a
  // sender_ref — the server resolves alert → newest).
  const [senders, setSenders] = useState<readonly ThreatSenderSummary[] | null>(null);
  // The search text the current `senders` answer (search spinner).
  const [sendersQuery, setSendersQuery] = useState<string | null>(null);
  // Bumped by SenderDetail after a block / trust / ignore so the list and
  // the BLOCKED count refresh at once instead of on the next poll.
  const [sendersReload, setSendersReload] = useState(0);
  const [selectedSender, setSelectedSender] = useState<ThreatSenderSummary | null>(null);
  // ADR-0248 S2 — probe-campaign rollups shown above the sender list.
  // Null = not loaded; empty array = no campaigns in the window.
  const [campaigns, setCampaigns] = useState<readonly ThreatCampaignSummary[] | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<ThreatCampaignSummary | null>(null);
  // sender_ref → campaign_id, used by the SenderCard chip. Keyed by
  // the server's first-page sender_refs; `undefined` means "no campaign".
  const [campaignBySender, setCampaignBySender] = useState<Readonly<Record<string, string>>>({});
  // Founder bug 2026-10-18: Suspicious segment carries a small
  // "Blocked · {n} →" link to Settings → Blocking. Hidden when there
  // are 0 blocks + blocking is off.
  const [suspiciousBlocking, setSuspiciousBlocking] = useState<{ live_blocks: number; mode: 'on' | 'off' } | null>(null);
  const pendingSenderRefRef = useRef<string | null>(
    typeof window === 'undefined' ? null : readThreatParam(window.location.href),
  );
  // Suspicious v2 — auto-select the first sender on first load when
  // nothing is deep-linked, so the panel never shows a stale selection
  // (e.g. a previously-open agent detail). Keyed on segment so a
  // different segment doesn't inherit the Suspicious selection.
  const didAutoSelectSenderRef = useRef(false);
  // Mirror of `selectedSender` so the groups poll can skip its
  // auto-select-first-group when a Suspicious sender is already open.
  // Updated below once the setter has run (same shape as the
  // pre-Suspicious-v2 `selectedThreatRef` guard that this replaces).
  const selectedSenderRef = useRef<ThreatSenderSummary | null>(null);
  selectedSenderRef.current = selectedSender;
  // URL state (founder bugs 2026-10-15): `?segment=`, `?list=`, `?record=`
  // (a record the merchant opened) and `?person=` are written on every
  // change by one effect below and read once here, on mount.
  // The list's last load failed (shown instead of a stale list; polls retry).
  const [loadError, setLoadError] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const u = new URL(window.location.href);
    // A record open from a person's timeline wins; the person is its "back".
    if (u.searchParams.get('record') !== null) return null;
    if (readSegmentParam(u.href) !== 'people' || u.searchParams.get(LIST_VIEW_PARAM) === 'visits') return null;
    return readPersonParam(u.href);
  });
  const [urlRecordId, setUrlRecordId] = useState<string | null>(() =>
    (typeof window === 'undefined' ? null : new URL(window.location.href).searchParams.get('record')));
  // The visitor a record was opened from (timeline RECORD link), for "← back".
  const [returnPersonId, setReturnPersonId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const u = new URL(window.location.href);
    return u.searchParams.get('record') !== null ? readPersonParam(u.href) : null;
  });
  const showPeopleCardsRef = useRef(false);
  const selectedPersonIdRef = useRef<string | null>(null);
  selectedPersonIdRef.current = selectedPersonId;
  const selectedGroupKeyRef = useRef<string | null>(null);
  selectedGroupKeyRef.current = selectedGroupKey;
  const selectedRef = useRef<MandateRecord | null>(null);
  selectedRef.current = selected;
  // The group a record was opened from (Activity tab), for "← back".
  const [returnGroupKey, setReturnGroupKey] = useState<string | null>(null);
  const openGroup = (g: VisitGroup): void => {
    setSelectedPersonId(null);
    setUrlRecordId(null);
    setReturnPersonId(null);
    setSelectedGroupKey(g.group_key);
    setSelected(g.representative as MandateRecord);
    setSelectedSender(null);
  };
  /** Suspicious v2 — open a sender group in the detail panel. */
  const openSender = (s: ThreatSenderSummary): void => {
    setSelected(null);
    setSelectedPersonId(null);
    setUrlRecordId(null);
    setReturnPersonId(null);
    setSelectedGroupKey(null);
    setReturnGroupKey(null);
    setSelectedCampaign(null);
    setSelectedSender(s);
  };
  /** ADR-0248 S2 — open a campaign in the detail panel. */
  const openCampaign = (c: ThreatCampaignSummary): void => {
    setSelected(null);
    setSelectedPersonId(null);
    setUrlRecordId(null);
    setReturnPersonId(null);
    setSelectedGroupKey(null);
    setReturnGroupKey(null);
    setSelectedSender(null);
    setSelectedCampaign(c);
  };
  // Detail layout: sticky side panel on desktop, full-screen replace panel
  // on mobile (tap a row → panel replaces the list with a ← BACK button).
  const isMobile = useIsBelowLg();
  // Mobile panel open state, kept SEPARATE from `selected`. The poll
  // auto-selects the first row (so the desktop side panel is never blank),
  // but that must NOT replace the list on load — the panel shows ONLY on
  // an explicit row tap or a `?record=` deep link.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Scroll ref on the desktop sticky detail pane. Used by the effect
  // below to reset scrollTop whenever the selection changes.
  const detailPaneRef = useRef<HTMLDivElement | null>(null);

  // Mobile swipe navigation (full-screen detail panel). Record the start
  // point only when the primary pointer is a horizontal touch gesture at
  // the surface of the panel; multi-touch (pinch, pull-to-refresh) and
  // taps on interactive descendants shouldn't trigger a row change.
  //
  // Thresholds (founder bug 2026-10-27 — "swipe gets stuck on some filters"):
  //   - MIN_DX = 50 px horizontal travel so a vertical scroll never fires swipe.
  //   - dx must dominate dy: `|dx| > |dy|` (strict), so an angled scroll
  //     doesn't flip the panel to the next row halfway through.
  //   - swipe is a NO-OP when the current nav list is empty or the panel
  //     has no selection (filter in flight, list just cleared): the handler
  //     clears the start point and returns silently so a stale swipe can't
  //     "stick" on the next render once records arrive.
  const SWIPE_MIN_DX = 50;
  const swipeTouchStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const onSwipeTouchStart = (e: React.TouchEvent): void => {
    // Multi-touch (pinch) — never swipe.
    if (e.touches.length !== 1) {
      swipeTouchStart.current = null;
      return;
    }
    const t = e.touches[0]!;
    swipeTouchStart.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onSwipeTouchEnd = (e: React.TouchEvent): void => {
    const start = swipeTouchStart.current;
    swipeTouchStart.current = null;
    if (start === null) return;
    // A gesture that lingers > 600 ms is probably a long press or a pause
    // mid-scroll, not a flick. Ignore.
    if (Date.now() - start.t > 600) return;
    const end = e.changedTouches[0];
    if (end === undefined) return;
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN_DX) return;
    if (Math.abs(dx) <= Math.abs(dy)) return;
    // Nothing to navigate to — swipe would be a no-op (and the stale
    // closure would still try). Return early so the user doesn't get the
    // feel of a "stuck" swipe during a filter transition.
    if (navLength === 0 || selectedIndex < 0) return;
    if (dx < 0) {
      if (canGoNext) goToNext();
    } else if (canGoPrev) goToPrev();
  };

  // Deep-link `?record=…` one-shot — the Dashboard's ActivityFeed rows
  // write this to open a specific record's detail drawer on mount.
  // Fetched independently (the list-poll effect can preserve / clear
  // selection on its own clock); on success the URL param is cleared
  // so a refresh after the user clicks around doesn't keep re-opening
  // the same drawer (mirrors the existing `?escalation=` flow).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let record_id: string | null = null;
    try {
      record_id = new URL(window.location.href).searchParams.get('record');
    } catch {
      return;
    }
    if (record_id === null || record_id.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const detail = await api.mandates.get(record_id as AuditRecordId);
        if (cancelled || detail === null) return;
        setSelectedGroupKey(null);
        setSelectedPersonId(null);
        setSelected(detail as MandateRecord);
        // Stays in the URL (round-trips a reload) until the merchant picks
        // something else; the URL-sync effect owns `?record=`.
        setUrlRecordId(detail.record_id);
        // A deep link is an explicit request for THIS record — open the
        // mobile drawer too (no-op on desktop, which shows the side panel).
        setDrawerOpen(true);
      } catch {
        // best-effort — if the fetch fails the drawer stays closed
        if (!cancelled) setUrlRecordId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  // Intentionally a mount-only effect (one-shot), matching `?escalation=`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link scroll anchor — captured from `?record=` at mount (the
  // initializer runs before the effect above clears the param). When the
  // deep-linked record is present in the loaded list, center its row +
  // flash it so arriving on a long list lands focus ON it, not the top.
  // (If the record is older than the loaded page it isn't in the list —
  // the detail drawer above still opens; only the row anchor is skipped.)
  //
  // The captured value is validated only as a safe DOM/token shape
  // (alphanumeric + `_`, bounded) — NOT as a specific id format. record_id
  // is a 26-char ULID (uppercase Crockford base32, e.g.
  // `01KWHRJSZ4ZQDZZM39BM427RVH`), so the earlier `[0-9a-f]` (lowercase
  // hex) guard rejected every real id and the anchor never fired. The
  // exact-match against `visibleRecords` below is the real correctness
  // gate; this guard only keeps `querySelector` injection-safe.
  const readRecordParam = (): string | null => {
    if (typeof window === 'undefined') return null;
    try {
      const v = new URL(window.location.href).searchParams.get('record');
      return v !== null && /^[A-Za-z0-9_]{1,64}$/.test(v) ? v : null;
    } catch {
      return null;
    }
  };
  const anchorRecordRef = useRef<string | null>(readRecordParam());
  // Deep-link PIN — same id, but persists past the one-shot anchor so the
  // 10s list poll can't reset the selection to the top row when the
  // deep-linked record is OFF the current page (Signed Transactions can
  // sit below page 1 of the mixed discovery+transactional list). Cleared
  // when the merchant explicitly clicks a different row.
  const pinnedRecordRef = useRef<string | null>(readRecordParam());
  // `?group=` (founder 2026-10-19): a shared link to a grouped row such as
  // "Unknown scanner" reopens that row, not only its newest visit. Read
  // once; the first grouped load that contains the row opens it.
  const pinnedGroupRef = useRef<string | null>((() => {
    if (typeof window === 'undefined') return null;
    try {
      const v = new URL(window.location.href).searchParams.get('group');
      return v !== null && v.length > 0 && v.length <= 200 ? v : null;
    } catch {
      return null;
    }
  })());
  const [flashRecordId, setFlashRecordId] = useState<string | null>(null);
  // Off-page deep-link fallback state. When the linked record isn't on the
  // first loaded page, the anchor effect (below) searches its id once to
  // surface it (the server matches `record_id ILIKE`), so an OLD settled
  // transaction still scrolls into view instead of only opening its detail.
  // `hasLoadedRef` gates the fallback until the initial page has arrived
  // (so a recent, on-page record keeps its full-list-in-context scroll and
  // is never needlessly filtered); `offpageSearchTriedRef` keeps it a
  // one-shot so it can't loop.
  const hasLoadedRef = useRef(false);
  const offpageSearchTriedRef = useRef(false);
  // The scroll+flash effect itself lives below `visibleRecords` (which it
  // reads), just after that memo is declared.

  // ADR-0059 §6 — lazy single-record fetch to surface the payload
  // canonical-JSON content. The list endpoint does NOT carry payload
  // (too heavy); we re-fetch the full detail when a row is selected.
  const [selectedPayload, setSelectedPayload] = useState<AuditRecordPayload | undefined>(undefined);
  // ADR-0204 Slice 3 — bot classification from single-record detail.
  const [selectedAiSummary, setSelectedAiSummary] = useState<BotSummary | undefined>(undefined);
  // ADR-0204 follow-up — agent_key + per-account dismissal state, both
  // only surfaced by the single-record projection. Threaded to
  // MandateDetail alongside `ai_summary` (see below) so
  // BotClassificationRow can render the dismiss affordance and hit
  // `POST /v1/bots/:agent_key/dismiss-action`.
  const [selectedAgentKey, setSelectedAgentKey] = useState<string | undefined>(undefined);
  const [selectedBotActionDismissed, setSelectedBotActionDismissed] =
    useState<boolean | undefined>(undefined);
  const [selectedConnectorCall, setSelectedConnectorCall] =
    useState<AuditConnectorCall | undefined>(undefined);
  // SETTLEMENT money breakdown for the detail SettlementPanel — fetched
  // with the single record on select. The LIST endpoint already carries
  // settlement (incl. the headline-relevant rail / total_minor /
  // financial_status / ceiling_minor) via the same allow-list extractor,
  // so the row card renders the ACTUAL settled total — not the agent-
  // signed `max_amount` cap — through `recordHeadline` (ADR-0063 /
  // ADR-0066). We still re-fetch on select to keep the SettlementPanel
  // sourced from the authoritative single-record read; it's stashed in
  // SEPARATE state (like the payload) so setting it doesn't re-trigger
  // the select effect.
  const [selectedSettlement, setSelectedSettlement] =
    useState<MandateRecord['settlement'] | undefined>(undefined);
  // ADR-0099 follow-up — the server-resolved ORIGINAL order for a refund
  // record. Like `settlement`, it is ONLY on the single-record route (the
  // LIST projection omits it), so it must be fetched on select and merged
  // at render — NOT into `selected` (the effect dependency, would loop).
  // Without this the refund detail's Original Order panel always rendered
  // its "could not be resolved" fallback even though the API returned it.
  const [selectedOriginalOrder, setSelectedOriginalOrder] =
    useState<MandateRecord['original_order']>(undefined);
  // ADR-0210 Slice 9 — the pages behind the record, single-record read only.
  const [selectedPageHits, setSelectedPageHits] = useState<MandateRecord['page_hits']>(undefined);
  // ops2026 UCP checkout (2026-10-21) — the full money breakdown + the
  // staff-test flag. Both live ONLY on the single-record projection:
  //   - `order_breakdown` is resolved from the UCP checkout session (per-
  //     line price, shipping, tax, ship-to, platform_host), too heavy for
  //     the list route to carry.
  //   - `staff_test` IS written on the list route today, but a safety-net
  //     merge keeps the Activity header's "SILL TEST" pill stable even on
  //     an older list projection that omits it.
  // Same stash-+-merge-at-render discipline as settlement / original_order
  // / page_hits above — never into `selected` (would loop the effect).
  const [selectedOrderBreakdown, setSelectedOrderBreakdown] =
    useState<MandateRecord['order_breakdown']>(undefined);
  const [selectedStaffTest, setSelectedStaffTest] = useState<boolean | undefined>(undefined);
  // Payment-record linked order (ADR-0157 follow-up 2026-10-25) — the
  // single-record route returns the ordered product + lines + protocol
  // so a settled_succeeded detail shows the full purchase story without
  // a second call. Stash + merge at render in the same discipline as
  // `settlement` / `original_order` / `order_breakdown` above.
  const [selectedLinkedOrder, setSelectedLinkedOrder] =
    useState<MandateRecord['linked_order']>(undefined);
  useEffect(() => {
    let cancelled = false;
    setSelectedPayload(undefined);
    setSelectedSettlement(undefined);
    setSelectedOriginalOrder(undefined);
    setSelectedPageHits(undefined);
    setSelectedAiSummary(undefined);
    setSelectedAgentKey(undefined);
    setSelectedBotActionDismissed(undefined);
    setSelectedConnectorCall(undefined);
    setSelectedOrderBreakdown(undefined);
    setSelectedStaffTest(undefined);
    setSelectedLinkedOrder(undefined);
    if (selected === null) return () => undefined;
    const record_id = selected.record_id;
    (async () => {
      try {
        const detail = await api.mandates.get(record_id);
        if (cancelled || detail === null) return;
        // The single-record fetch carries detail-only fields (the
        // SETTLEMENT money breakdown) the list projection omits. Stash it
        // in a SEPARATE state (like the payload) and merge at render — NOT
        // into `selected`, which is this effect's dependency (would loop).
        const settlement = (detail as MandateRecord).settlement;
        if (settlement !== undefined) {
          setSelectedSettlement(settlement);
        }
        // ADR-0099 follow-up — refund records carry the server-resolved
        // original order ONLY on this single-record read; stash + merge at
        // render so the Original Order panel populates (line + buyer reveal)
        // instead of the unresolved fallback.
        const original_order = (detail as MandateRecord).original_order;
        if (original_order !== undefined) {
          setSelectedOriginalOrder(original_order);
        }
        const page_hits = (detail as MandateRecord).page_hits;
        if (page_hits !== undefined) {
          setSelectedPageHits(page_hits);
        }
        const payload = (detail as AuditRecord & { payload?: AuditRecordPayload }).payload;
        if (payload !== undefined) {
          setSelectedPayload(payload);
        }
        // ADR-0204 Slice 3 — extract ai_summary from single-record detail.
        const ai_summary = detail.ai_summary;
        if (ai_summary !== undefined) {
          setSelectedAiSummary(ai_summary);
        }
        // ADR-0204 follow-up — agent_key + bot_action_dismissed ride the
        // same single-record projection. Both are optional; agent_key is
        // absent when no matching agent_observed row exists, and
        // bot_action_dismissed defaults to false when omitted.
        const agent_key = detail.agent_key;
        if (agent_key !== undefined) {
          setSelectedAgentKey(agent_key);
        }
        const bot_action_dismissed = detail.bot_action_dismissed;
        if (bot_action_dismissed !== undefined) {
          setSelectedBotActionDismissed(bot_action_dismissed);
        }
        const connector_call = detail.connector_call;
        if (connector_call !== undefined) {
          setSelectedConnectorCall(connector_call);
        }
        // ops2026 UCP checkout (2026-10-21) — the money breakdown + the
        // staff-test flag the single-record route carries. The merge at
        // render reaches MandateDetailV3 so the Overview Order section,
        // Ship to, Placed by, and the "SILL TEST" header pill all read
        // the authoritative single-record projection.
        const order_breakdown = (detail as MandateRecord).order_breakdown;
        if (order_breakdown !== undefined) {
          setSelectedOrderBreakdown(order_breakdown);
        }
        const staff_test = (detail as MandateRecord).staff_test;
        if (staff_test !== undefined) {
          setSelectedStaffTest(staff_test);
        }
        const linked_order = (detail as MandateRecord).linked_order;
        if (linked_order !== undefined) {
          setSelectedLinkedOrder(linked_order);
        }
      } catch {
        // Best-effort; UI degrades to "no payload".
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, selected]);
  // Today as YYYY-MM-DD in UTC — caps the date inputs so a merchant cannot
  // select a future date (the audit_record server-side rejects future
  // timestamps anyway, but a future `from` produces an empty result set
  // with no useful signal). Recomputed once per render — cheap enough.
  // Today in the display zone: the date range is days in that zone.
  const today = todayInZone(displayTz);
  // Advanced-filter row collapse state — defaults open when any of the
  // advanced filters is set on mount so navigate-back preserves context;
  // collapses by default on a clean load to reduce visual weight.
  // The Audit log view lives in this row too, so a ?list=visits link
  // opens it (ADR-0246).
  const [advOpen, setAdvOpen] = useState<boolean>(
    // Auto-expand on mount when ANY advanced filter is active — including
    // PROTOCOL + STATUS, which also live in Row 2. The ProtocolMixCard
    // ("How agents reach you") and the Overview-card status links both
    // deep-link into Activity with these set; without the auto-expand the
    // chip row stays collapsed and the merchant sees a filtered list with
    // no visible reason.
    () => filter !== 'all'
      || status !== 'all'
      || protocol !== 'all'
      || dateFrom.length > 0
      || dateTo.length > 0
      || listView === 'visits',
  );
  // Agent-key filter — set by "View all activity" in the bot panel.
  // Cleared by clearFilters() or the × chip above the list.
  const [agentKeyFilter, setAgentKeyFilter] = useState<string | undefined>(undefined);
  // Founder 2026-10-23 — the selected UCP checkout session for the
  // `?protocol=ucp` view. Separate state from `selected` (an audit record)
  // so a UCP session with no signed record yet still has a right-panel home.
  const [selectedUcpSession, setSelectedUcpSession] = useState<string | null>(null);

  // A site that can take payments keeps its order filters in More filters
  // and its Transactions chip even before the first order arrives.
  const canTakePayments = activeSite !== null && activeSite !== undefined
    && siteMode(activeSite) === 'transactions_ready';

  const advActiveCount =
    (filter !== 'all' ? 1 : 0)
    + (listView === 'visits' && !groupsUnavailable ? 1 : 0)
    + (status !== 'all' ? 1 : 0)
    // ADR-0253 §A — the PROTOCOL chip lives in Row 2 (More filters) too,
    // so count it alongside identity + date + log view. Without it, a
    // deep link like `?protocol=a2a` opens with the chip filter applied
    // but no "ACTIVE" badge on the collapsed Row 2 — the merchant can't
    // see WHY the list is narrowed.
    + (protocol !== 'all' ? 1 : 0)
    + (dateFrom.length > 0 ? 1 : 0)
    + (dateTo.length > 0 ? 1 : 0);

  // Step C: people are a server-side chip now (the old client-side
  // hide-humans toggle is gone), so every loaded row is visible.
  const visibleRecords = records;
  const visibleGroups = groups;
  const selectedGroup = useMemo(
    () => (selectedGroupKey === null ? undefined : groups.find((g) => g.group_key === selectedGroupKey)),
    [groups, selectedGroupKey],
  );
  const returnGroup = useMemo(
    () => (returnGroupKey === null || selectedGroupKey !== null ? undefined : groups.find((g) => g.group_key === returnGroupKey)),
    [groups, returnGroupKey, selectedGroupKey],
  );

  // Swipe navigation between rows on mobile (groups in the grouped
  // view). Founder bug 2026-10-18: the Suspicious attack cards had no
  // swipe + no "N / M" pager because the generic grouped-view path
  // only knew about `visibleGroups` and `records`. When the selected
  // thing is a sender (`selectedSender`), walk the `senders` list.
  // Founder 2026-10-18: campaign cards join the same swipe/pager order
  // the list shows them in — campaigns first, then address cards.
  const suspiciousNavItems = useMemo((): ReadonlyArray<
    { readonly kind: 'campaign'; readonly c: ThreatCampaignSummary } | { readonly kind: 'sender'; readonly s: ThreatSenderSummary }
  > => [
    ...(campaigns ?? []).map((c) => ({ kind: 'campaign' as const, c })),
    ...(senders ?? []).map((s) => ({ kind: 'sender' as const, s })),
  ], [campaigns, senders]);
  const isSuspiciousNav = segment === 'suspicious' && (selectedSender !== null || selectedCampaign !== null)
    && suspiciousNavItems.length > 0;
  const selectedIndex = useMemo(() => {
    if (isSuspiciousNav) {
      return suspiciousNavItems.findIndex((it) => (it.kind === 'campaign'
        ? selectedCampaign?.campaign_id === it.c.campaign_id
        : selectedSender?.sender_ref === it.s.sender_ref));
    }
    if (effectiveView === 'agents') {
      return selectedGroupKey === null ? -1 : visibleGroups.findIndex((g) => g.group_key === selectedGroupKey);
    }
    return selected === null ? -1 : records.findIndex((r) => r.record_id === selected.record_id);
  }, [isSuspiciousNav, suspiciousNavItems, selectedSender, selectedCampaign, effectiveView, visibleGroups, selectedGroupKey, records, selected]);
  const navLength = isSuspiciousNav
    ? suspiciousNavItems.length
    : effectiveView === 'agents' ? visibleGroups.length : records.length;
  const canGoPrev = selectedIndex > 0;
  const canGoNext = selectedIndex >= 0 && selectedIndex < navLength - 1;
  const goToIndex = (i: number): void => {
    if (isSuspiciousNav) {
      const it = suspiciousNavItems[i];
      if (it?.kind === 'campaign') openCampaign(it.c);
      else if (it?.kind === 'sender') openSender(it.s);
      return;
    }
    if (effectiveView === 'agents') {
      const g = visibleGroups[i];
      if (g !== undefined) { setReturnGroupKey(null); openGroup(g); }
    } else {
      const r = records[i];
      if (r !== undefined) setSelected(r);
    }
  };
  const goToPrev = (): void => { if (canGoPrev) goToIndex(selectedIndex - 1); };
  const goToNext = (): void => { if (canGoNext) goToIndex(selectedIndex + 1); };
  // Deep-link scroll anchor (see `anchorRecordRef` above) — once the
  // linked record shows up in the visible list, center + flash its row.
  // When it's OFF the first loaded page (e.g. an old Signed Transaction
  // buried under discovery traffic), fall back to searching its id once so
  // the list surfaces it and the same scroll+flash can fire.
  useEffect(() => {
    const id = anchorRecordRef.current;
    if (id === null) return undefined;
    // Grouped view: the deep-linked record opens in the detail panel (the
    // pin keeps it there); its visit sits inside an agent row, so there is
    // no per-record row to scroll to or search for.
    if (effectiveView === 'agents') return undefined;
    if (!visibleRecords.some((r) => r.record_id === id)) {
      // Not on this page. If the first load has arrived and we haven't yet
      // tried, surface the record by searching its id — but never override
      // a search the merchant is already typing.
      if (
        hasLoadedRef.current
        && !offpageSearchTriedRef.current
        && query.trim().length === 0
      ) {
        offpageSearchTriedRef.current = true;
        setQuery(id);
      }
      return undefined;
    }
    anchorRecordRef.current = null; // consume — one-shot
    const node =
      typeof document !== 'undefined'
        ? document.querySelector(`[data-record-id="${id}"]`)
        : null;
    // `scrollIntoView` is unimplemented in jsdom — guard so the anchor
    // effect degrades to just the flash under test.
    if (node instanceof HTMLElement && typeof node.scrollIntoView === 'function') {
      const reduce =
        typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      node.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    }
    setFlashRecordId(id);
    const t = setTimeout(() => setFlashRecordId(null), 1400);
    return () => clearTimeout(t);
  }, [visibleRecords, query, effectiveView]);
  const [newRecordIds, setNewRecordIds] = useState<Set<string>>(() => new Set());
  /** Snapshot of record IDs from the previous poll of the CURRENT page,
   *  used to compute the NEW-pill diff. A ref (not state) so updating it
   *  doesn't trigger a re-render. */
  const seenIdsRef = useRef<Set<string>>(new Set());

  // ADR-0051 §2.2 — when the active site is a soft-deleted archive
  // (`deleted_at !== null`), the records list is a read-only view of
  // history. The site can no longer accept agent traffic (the embed
  // snippet was de-registered at delete time), so polling would burn
  // a 10s-interval API call to a guaranteed-stable result set. We
  // disable polling on archive sites and render a persistent
  // DELETED-SITE banner above the list. Bundle export (per-record)
  // still works — the audit-bundle endpoint has no deleted-site
  // filter, and the records' Merkle chain is intact.
  const isDeletedSite = activeSite?.deleted_at !== undefined
    && activeSite.deleted_at !== null;

  // Fetch the current page and poll it. Offset pagination: the server
  // returns this page's slice plus total_count / total_pages. Polling
  // refreshes the CURRENT page in place (10s, visible-tab only) so newly
  // signed records surface; the NEW pill flags IDs that were not on the
  // page at the previous poll. Page changes (pager clicks) and filter
  // changes both re-run this effect via the dependency array — filter
  // setters reset `page` to 1 so a new filter lands on the first page.
  //
  // ADR-0051 §2.2 — polling is skipped on a soft-deleted site (the
  // record set is stable; no new records will ever land). The initial
  // fetch still runs so the archive renders.
  useEffect(() => {
    if (!activeSite || effectiveView !== 'visits') return;
    let cancelled = false;
    const site_id = activeSite.site_id;
    seenIdsRef.current = new Set();
    let first_load = true;

    const fetchPage = async (): Promise<void> => {
      if (first_load) {
        setLoading(true);
        // A new filter never shows the previous filter's rows while it
        // loads, or after it fails (founder bug 2: "nothing happened").
        setRecords([]);
        setLoadError(false);
      }
      try {
        const result = await api.mandates.listObserved(site_id, {
          // Skip q when agent_key filter is active: the display name in the
          // search box is an AI-generated label, not a stored field, so sending
          // it as q would AND-match to 0 results.
          ...(agentKeyFilter === undefined && query.length > 0 ? { q: query } : {}),
          identity: filter,
          // "View all activity" for one agent shows every visit it made.
          ...(agentKeyFilter === undefined ? { segment } : {}),
          status,
          date_from: dateFrom,
          date_to: dateTo,
          tz: displayTz,
          page,
          ...(agentKeyFilter !== undefined ? { agent_key: agentKeyFilter } : {}),
          ...(protocol !== 'all' ? { protocol } : {}),
        });
        if (cancelled) return;

        const next = result.records;
        const next_ids = new Set(next.map((r) => r.record_id));
        if (!first_load) {
          // Diff for the NEW pill — fresh records relative to what was on
          // this page at the previous poll.
          const fresh_ids = [...next_ids].filter((id) => !seenIdsRef.current.has(id));
          if (fresh_ids.length > 0) {
            setNewRecordIds((cur) => {
              const merged = new Set(cur);
              for (const id of fresh_ids) merged.add(id);
              return merged;
            });
            for (const id of fresh_ids) {
              setTimeout(() => {
                setNewRecordIds((cur) => {
                  if (!cur.has(id)) return cur;
                  const next_set = new Set(cur);
                  next_set.delete(id);
                  return next_set;
                });
              }, NEW_RECORD_BADGE_MS);
            }
          }
        }
        seenIdsRef.current = next_ids;

        // Offset pages REPLACE the visible list — the page slice is the
        // whole of what's shown. Total count / pages drive the pager.
        setRecords(next);
        // Mark the first page as arrived so the off-page deep-link fallback
        // (anchor effect) can distinguish "record genuinely not on the
        // page" from "list hasn't loaded yet".
        hasLoadedRef.current = true;
        setTotalCount(result.total_count ?? next.length);
        setTotalPages(result.total_pages ?? (next.length > 0 ? 1 : 0));
        // Keep the selected record if it's still on this page; also keep a
        // deep-linked PINNED record even when it's off the current page (so
        // the poll can't yank a Signed-Transactions/Live-Activity deep link
        // back to the top row). Otherwise fall back to the first row (or
        // clear when the page is empty).
        setSelected((prev) => {
          if (prev !== null && next_ids.has(prev.record_id)) return prev;
          if (prev !== null && prev.record_id === pinnedRecordRef.current) return prev;
          return next[0] ?? null;
        });
        setLoadError(false);
      } catch {
        // The poll retries; until then say so instead of a stale list.
        if (!cancelled && first_load) setLoadError(true);
      } finally {
        if (!cancelled && first_load) {
          setLoading(false);
          first_load = false;
        }
      }
    };

    void fetchPage();

    // Skip polling on archive sites (ADR-0051 §2.2). The initial fetch
    // still hydrated the list above; the record set is stable from
    // here on out, so there is nothing for a 10s tick to do but burn
    // an API call.
    if (isDeletedSite) {
      return () => {
        cancelled = true;
      };
    }

    const handle = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchPage();
    }, MANDATES_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [api, activeSite, query, filter, segment, status, dateFrom, dateTo, displayTz, page, agentKeyFilter, isDeletedSite, effectiveView]);

  // Visits panel Step B — the grouped list. Same filters, same 10s poll
  // and archive rule as the per-record list above; the server groups over
  // every matching record, so the totals are true for the range.
  // Person cards are people, so they only belong under filters that can
  // include people: no Confirmed / Mismatch identity, and no status other
  // than Observed (people are never Confirmed, a Mismatch or an order).
  // Otherwise the server's grouped list is already empty for People and
  // the cards would be the only (wrong) rows (filters sweep 2026-10-14).
  const showPeopleCards = effectiveView === 'agents' && segment === 'people' && http !== null
    && filter === 'all' && (status === 'all' || status === 'observed');
  showPeopleCardsRef.current = showPeopleCards;
  const groupQuery: AuditQuery = useMemo(() => ({
    ...(query.length > 0 ? { q: query } : {}),
    identity: filter,
    segment,
    status,
    date_from: dateFrom,
    date_to: dateTo,
    tz: displayTz,
    ...(protocol !== 'all' ? { protocol } : {}),
  }), [query, filter, segment, status, dateFrom, dateTo, displayTz, protocol]);
  useEffect(() => {
    if (!activeSite || effectiveView !== 'agents') return;
    const list = api.mandates.listAgentGroups;
    if (list === undefined) return;
    let cancelled = false;
    const site_id = activeSite.site_id;
    let first_load = true;

    const fetchGroups = async (): Promise<void> => {
      if (first_load) {
        setLoading(true);
        setGroups([]);
        setLoadError(false);
      }
      try {
        const res = await list.call(api.mandates, site_id, { ...groupQuery, page });
        if (cancelled) return;
        setLoadError(false);
        setGroups(res.groups);
        setGroupTotals(res.totals);
        setSegmentCounts(res.segment_counts ?? null);
        setTotalCount(res.total_count);
        setTotalPages(res.total_pages);
        hasLoadedRef.current = true;
        // Keep the open group (its counts refresh with the list); keep a
        // single record opened from a group, a one-record row or a deep
        // link; otherwise open the first row so the desktop panel is
        // never blank.
        const pinnedGroup = pinnedGroupRef.current;
        if (pinnedGroup !== null) {
          pinnedGroupRef.current = null;
          const match = res.groups.find((g) => g.group_key === pinnedGroup);
          if (match !== undefined && pinnedRecordRef.current === null) {
            openGroup(match);
            setDrawerOpen(true);
            return;
          }
        }
        const curKey = selectedGroupKeyRef.current;
        if (selectedPersonIdRef.current !== null) return;
        // People chip with person cards: the people fetch opens the first
        // person, not the "Earlier visitors" row.
        if (showPeopleCardsRef.current && curKey === null) return;
        // A threat is open in the detail panel — leave it alone.
        // Without this guard the poll's auto-select wiped the
        // sender selection on its next tick and opened the first
        // group, giving the merchant a brief "two cards stacked"
        // view (founder feedback 2026-10-17).
        if (selectedSenderRef.current !== null) return;
        if (curKey !== null && res.groups.some((g) => g.group_key === curKey)) return;
        if (curKey === null && (selectedRef.current !== null || pinnedRecordRef.current !== null)) return;
        const head = res.groups[0];
        if (head !== undefined) openGroup(head);
        else { setSelectedGroupKey(null); setSelected(null); }
      } catch (err) {
        // An API without the grouped route: fall back to every visit.
        if (!cancelled && err instanceof ApiError && err.kind === 'not_found') setGroupsUnavailable(true);
        else if (!cancelled && first_load) setLoadError(true);
      } finally {
        if (!cancelled && first_load) {
          setLoading(false);
          first_load = false;
        }
      }
    };

    void fetchGroups();
    if (isDeletedSite) {
      return () => { cancelled = true; };
    }
    const handle = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchGroups();
    }, GROUPS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, activeSite, groupQuery, page, isDeletedSite, effectiveView]);

  // ADR-0243 — the People chip lists one card per tracked visitor. The
  // people route takes the date range (it defaults to the last 90 days);
  // its search matches the card (browser, OS, device, place). Visits that predate capture stay in the
  // grouped 'people' row, shown as "Earlier visitors".

  useEffect(() => {
    if (!showPeopleCards || !activeSite || http === null) { setPeople(null); return; }
    let cancelled = false;
    const site_id = activeSite.site_id;
    const fetchPeople = async (): Promise<void> => {
      try {
        const res = await listPeople(http, site_id, {
          ...(dateFrom.length > 0 ? { date_from: dateFrom } : {}),
          ...(query.length > 0 ? { q: query } : {}),
          tz: displayTz,
          ...(dateTo.length > 0 ? { date_to: dateTo } : {}),
          page,
        });
        if (cancelled) return;
        setPeople(res.people);
        setPeopleHiddenForSupport(false);
        setPeoplePaging({ total: res.total_count, pages: res.total_pages });
        const head = res.people[0];
        // Open the first person when nothing is open (desktop panel).
        if (head !== undefined && selectedPersonIdRef.current === null && selectedGroupKeyRef.current === null && selectedRef.current === null) {
          setSelectedPersonId(head.person_id);
        }
      } catch (err) {
        if (!cancelled && err instanceof ApiError && err.server_reason === SUPPORT_READ_ONLY_REASON) {
          setPeopleHiddenForSupport(true);
        }
        // Keep the last good list (or none) on a failed poll: an empty list
        // would relabel the row "Earlier visitors" while the server has
        // already moved tracked visits out of it (security review nit 2).
      }
    };
    void fetchPeople();
    if (isDeletedSite) return () => { cancelled = true; };
    const handle = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchPeople();
    }, GROUPS_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(handle); };
  }, [showPeopleCards, activeSite, http, dateFrom, dateTo, displayTz, query, page, isDeletedSite, peopleReload]);

  // Founder bug 2026-10-18: lightweight fetch of the per-site
  // blocking count for the Suspicious segment header link.
  useEffect(() => {
    if (segment !== 'suspicious' || activeSite === undefined || activeSite === null || http === null) {
      setSuspiciousBlocking(null);
      return;
    }
    const site_id = activeSite.site_id;
    let cancelled = false;
    void getBlocking(http, site_id)
      .then((r) => {
        if (cancelled || r == null || r.blocking == null) return;
        setSuspiciousBlocking({
          live_blocks: r.counts?.live_blocks ?? 0,
          mode: r.blocking.mode === 'on' ? 'on' : 'off',
        });
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [segment, activeSite, http, sendersReload]);

  // Suspicious v2 (2026-10-17) — grouped-per-sender list. One row per
  // sender IP, sorted unhandled-first server-side. Deep link
  // `?threat=tht_X` passes X as the sender_ref; the server resolves it
  // to the group's newest alert id.
  useEffect(() => {
    if (segment !== 'suspicious' || activeSite === undefined || activeSite === null || http === null) {
      setSenders(null);
      // Allow a fresh auto-select the next time Suspicious opens.
      didAutoSelectSenderRef.current = false;
      return;
    }
    if (effectiveView !== 'agents') { setSenders(null); return; }
    const site_id = activeSite.site_id;
    let cancelled = false;
    const fetchSenders = async (): Promise<void> => {
      try {
        const res = await listThreatSenders(http, site_id, {
          ...(dateFrom.length > 0 ? { date_from: dateFrom } : {}),
          ...(dateTo.length > 0 ? { date_to: dateTo } : {}),
          ...(query.trim().length > 0 ? { q: query } : {}),
        });
        if (cancelled) return;
        setSenders(res.senders);
        setSendersQuery(query);
        setCampaignBySender(res.campaign_by_sender ?? {});
        const pending = pendingSenderRefRef.current;
        // Spec feedback: on first Suspicious load with ≥1 sender and
        // nothing deep-linked, auto-select the first row so the
        // panel never shows a stale selection (desktop only; mobile
        // keeps the list in view). The ref only flips the first
        // time; subsequent polls do NOT snap the selection back.
        if (pending === null
          && !didAutoSelectSenderRef.current
          && !isMobile
          && res.senders.length > 0
          && selectedSender === null
        ) {
          didAutoSelectSenderRef.current = true;
          openSender(res.senders[0]!);
        }
        if (pending !== null) {
          const match = res.senders.find((s) => s.sender_ref === pending);
          if (match !== undefined) {
            pendingSenderRefRef.current = null;
            openSender(match);
            setDrawerOpen(true);
            if (typeof window !== 'undefined') {
              try {
                const url = new URL(window.location.href);
                if (url.searchParams.has('threat')) {
                  url.searchParams.delete('threat');
                  window.history.replaceState(window.history.state, '', url.toString());
                }
              } catch { /* URL hygiene is enhancement only */ }
            }
          } else {
            // The deep link points at an alert id that no longer heads
            // its group — the server detail endpoint resolves either
            // way, so render that sender_ref on first load.
            pendingSenderRefRef.current = null;
            openSender({
              sender_ref: pending,
              sender_ip_display: '',
              ip_status: 'hidden',
              country: null, region: null, as_org: null,
              network_type: 'unknown',
              max_severity: 'medium',
              any_succeeded: false,
              class_labels: [], class_summary: '',
              alert_count: 0, requests_display: '',
              request_count: 0,
              first_seen: new Date().toISOString(),
              last_seen: new Date().toISOString(),
              state: 'none', block_expires_at: null,
            });
            setDrawerOpen(true);
          }
        }
      } catch { /* keep prior list on failed poll */ }
    };
    void fetchSenders();
    if (isDeletedSite) return () => { cancelled = true; };
    const handle = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchSenders();
    }, GROUPS_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(handle); };
  }, [segment, activeSite, http, dateFrom, dateTo, effectiveView, isDeletedSite, query, sendersReload]);

  // ADR-0248 S2 — probe-campaign rollups (list only; detail opens on
  // click). Fetched alongside the sender list on the same segment /
  // site switch; shares the sender poll cadence.
  useEffect(() => {
    if (segment !== 'suspicious' || activeSite === undefined || activeSite === null || http === null) {
      setCampaigns(null);
      setSelectedCampaign(null);
      return;
    }
    if (effectiveView !== 'agents') { setCampaigns(null); return; }
    const site_id = activeSite.site_id;
    let cancelled = false;
    const fetchCampaigns = async (): Promise<void> => {
      try {
        const res = await listThreatCampaigns(http, site_id, {
          ...(dateFrom.length > 0 ? { date_from: dateFrom } : {}),
          ...(dateTo.length > 0 ? { date_to: dateTo } : {}),
        });
        if (cancelled) return;
        setCampaigns(res.campaigns);
      } catch { /* keep prior list on failed poll */ }
    };
    void fetchCampaigns();
    if (isDeletedSite) return () => { cancelled = true; };
    const handle = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchCampaigns();
    }, GROUPS_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(handle); };
  }, [segment, activeSite, http, dateFrom, dateTo, effectiveView, isDeletedSite, sendersReload]);

  // One way to open a single record (row click, timeline RECORD link,
  // Activity): it always wins the detail panel over a person or group.
  const openRecord = (rec: MandateRecord, returnPerson: string | null = null): void => {
    pinnedRecordRef.current = null;
    setSelectedPersonId(null);
    setSelectedGroupKey(null);
    setReturnPersonId(returnPerson);
    setSelected(rec);
    setUrlRecordId(rec.record_id);
    setSelectedSender(null);
    setSelectedCampaign(null);
    setDrawerOpen(true);
  };
  // A chip or list-mode change starts from the new list's first row.
  const clearSelection = (): void => {
    pinnedRecordRef.current = null;
    setSelectedPersonId(null);
    setSelectedGroupKey(null);
    setReturnGroupKey(null);
    setReturnPersonId(null);
    setSelected(null);
    setUrlRecordId(null);
    setSelectedSender(null);
    setSelectedCampaign(null);
  };

  const openPerson = (p: PersonSummary): void => {
    setUrlRecordId(null);
    setReturnPersonId(null);
    pinnedRecordRef.current = null;
    setReturnGroupKey(null);
    setSelectedGroupKey(null);
    setSelected(null);
    setSelectedPersonId(p.person_id);
    setSelectedSender(null);
    setSelectedCampaign(null);
    setDrawerOpen(true);
  };

  // "View all activity" (agent filter) ignores the chips and fills the
  // search box with a display name the server cannot match. A chip or a
  // list-mode change leaves that filter, so drop it and its search text;
  // before, the chip looked selected but changed nothing, and going back
  // to grouped searched the display name (empty list).
  const dropAgentFilter = (): void => {
    if (agentKeyFilter === undefined) return;
    setAgentKeyFilter(undefined);
    setQuery('');
  };
  const changeListView = (v: ListView): void => {
    dropAgentFilter();
    setListView(v);
    writeListView(v);
    setPage(1);
    // Founder bug 1: a person selected under the People cards used to stay
    // selected in every-visit mode and win the detail panel over any row.
    clearSelection();
  };

  // Fetch per-agent stats (visit + action counts) for every verified agent
  // on the current page in a single batch query. Runs after each page load.
  useEffect(() => {
    if (http === null || activeSite == null || records.length === 0 || effectiveView !== 'visits') return;
    const site_id = activeSite.site_id;
    const agent_ids = [
      ...new Set(
        records
          .map((r) => r.identification_match?.agent_id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0),
      ),
    ];
    if (agent_ids.length === 0) return;

    let cancelled = false;
    void http
      .request<{ stats: Array<{ agent_id: string; visit_count: number; action_count: number }> }>({
        method: 'GET',
        path: `/v1/sites/${site_id}/agent-stats?agent_ids=${encodeURIComponent(agent_ids.join(','))}`,
      })
      .then((res) => {
        if (cancelled || res === undefined) return;
        setAgentStats((prev) => {
          const next = new Map(prev);
          for (const stat of res.stats) {
            next.set(stat.agent_id, {
              visit_count: stat.visit_count,
              action_count: stat.action_count,
            });
          }
          return next;
        });
      })
      .catch(() => { /* best-effort — stats are non-critical */ });
    return () => { cancelled = true; };
  }, [http, records, activeSite, effectiveView]);

  // Reset the sticky detail pane's scroll on EVERY selection change
  // (founder feedback 2026-10-17 — opening a short threat after a
  // long agent detail used to leave the pane scrolled down, which
  // read as "two cards stacked"). One DOM write, no observer.
  useEffect(() => {
    const el = detailPaneRef.current;
    if (el !== null) el.scrollTop = 0;
  }, [selected, selectedPersonId, selectedSender]);

  // The one writer of the panel's URL state (read once on mount above):
  // chip, the opened record and the open visitor. `?list=` is written by
  // writeListView. replaceState: these are view state, not history.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      const set = (k: string, v: string | null): void => {
        if (v === null) url.searchParams.delete(k); else url.searchParams.set(k, v);
      };
      set('segment', segment === 'bots' ? null : segment);
      set('record', urlRecordId);
      set('group', urlRecordId === null ? selectedGroupKey : null);
      set('person', selectedPersonId ?? (urlRecordId !== null ? returnPersonId : null));
      set('identity', filter === 'all' ? null : filter);
      set('status', status === 'all' ? null : status);
      // ADR-0253 §A — persist the protocol chip to the URL so the browser
      // Back/Forward buttons restore it and the Overview card's deep link
      // round-trips.
      set('protocol', protocol === 'all' ? null : protocol);
      // Date range round-trips too (`readDateParam` reads it on mount).
      set('date_from', dateFrom.length > 0 ? dateFrom : null);
      set('date_to', dateTo.length > 0 ? dateTo : null);
      if (url.toString() !== window.location.href) window.history.replaceState(window.history.state, '', url.toString());
    } catch {
      /* URL state is an enhancement */
    }
  }, [segment, filter, status, protocol, urlRecordId, selectedPersonId, returnPersonId, selectedGroupKey, dateFrom, dateTo]);

  const hasActiveFilters =
    segment !== 'bots'
    || filter !== 'all'
    || status !== 'all'
    || protocol !== 'all'
    || query.length > 0
    || dateFrom.length > 0
    || dateTo.length > 0;

  // Drift diagnostic for the empty-state: a previously-live site whose
  // beacons have gone quiet for >24h is almost always a key-rotation
  // drift between the deployed snippet and `site_key_hash`. Only meaningful
  // when no filters are active — an empty list under a filter is the
  // filter's doing, not a dead embed.
  //
  // ADR-0051 §2.2 — the drift hint is meaningless on a deleted-site
  // archive (the snippet was de-registered at delete time; quietness
  // is the expected state). Suppress it so the empty-state copy
  // reflects "archive with no records" rather than "your install
  // broke."
  const emptyStaleSignal =
    !hasActiveFilters && activeSite && isLiveSite(activeSite) && !isDeletedSite
      ? siteStaleSignal(activeSite.last_activity_at)
      : null;

  const clearFilters = (): void => {
    setQuery('');
    setSegment('bots');
    setFilter('all');
    setStatus('all');
    setProtocol('all');
    setDateFrom('');
    setDateTo('');
    setAgentKeyFilter(undefined);
    setPage(1);
  };

  // ADR-0253 §A — protocol chip handler. Changing the chip resets to
  // page 1 and clears any opened record (same posture as every other
  // filter mutation).
  const changeProtocol = (v: string): void => {
    setProtocol(v);
    setPage(1);
  };

  // Filter mutations reset to page 1 in the same batched render, so a
  // changed filter lands on the first page of its result set with a
  // single fetch (no stale-page round trip).
  const changeQuery = (v: string): void => { setQuery(v); setAgentKeyFilter(undefined); setPage(1); };
  const changeFilter = (v: IdentitySegment): void => {
    setFilter(v);
    setPage(1);
  };
  const changeSegment = (v: VisitSegmentFilter): void => {
    dropAgentFilter();
    setSegment(v);
    // Outcome pills belong to Transactions only.
    if (v !== 'orders') setStatus('all');
    // The new list's first row (or first person) opens; a record or group
    // from the previous chip never lingers in the panel (founder bug 2).
    clearSelection();
    setPage(1);
  };
  const changeStatus = (v: AuditStatusFilter): void => { setStatus(v); setPage(1); };
  // From More filters an order outcome also switches the list to Transactions
  // (outcomes belong to signed orders only).
  const pickOrderStatus = (v: AuditStatusFilter): void => {
    if (segment !== 'orders') changeSegment('orders');
    changeStatus(v);
  };
  // Order-outcome pills: under the Transactions chip, or in More filters
  // for a site that can take payments.
  const renderStatusPills = (
    onPick: (v: AuditStatusFilter) => void,
    current: AuditStatusFilter | null,
  ): JSX.Element[] =>
    STATUS_FILTERS.map((s) => {
      const sel = current === s.id;
      const accent = toneFg(C, s.tone);
      return (
        <button
          type="button"
          key={s.id}
          onClick={() => onPick(s.id)}
          className="px-3 py-1.5"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1,
            color: sel ? accent : C.muted,
            background: sel && s.tone !== 'neutral' ? toneBg(C, s.tone) : sel ? C.surface2 : 'transparent',
            border: `1px solid ${sel ? (s.tone === 'neutral' ? C.border : accent) : 'transparent'}`,
            borderRadius: 2,
            cursor: 'pointer',
          }}
        >
          {s.label.toUpperCase()}
        </button>
      );
    });

  const changeDateFrom = (v: string): void => { setDateFrom(v); setPage(1); };
  const changeDateTo = (v: string): void => { setDateTo(v); setPage(1); };

  // People cards: the pager follows the people list (the grouped list
  // under People is at most the one "Earlier visitors" row, on page 1).
  const peoplePager = people !== null && peoplePaging.pages > totalPages;
  const pagerTotalPages = peoplePager ? peoplePaging.pages : totalPages;
  // Pager navigation — clamp into [1, totalPages] so a stale jump can't
  // overshoot. `totalPages` is 0 only on an empty result set, where the
  // pager is hidden anyway.
  const goToPage = (p: number): void => {
    const max = Math.max(1, pagerTotalPages);
    setPage(Math.min(Math.max(1, p), max));
  };

  const headerSubtitle = useMemo(() => {
    if (loading) return 'Loading audit records…';
    const n = effectiveView === 'agents' ? groupTotals?.records ?? 0 : totalCount;
    const noun = `audit record${n === 1 ? '' : 's'}`;
    const count = n.toLocaleString('en-US');
    // The default chip is All bots (people excluded), so the count is never
    // "everything in this site" (filters cleanup 2026-10-15).
    if (hasActiveFilters) return `${count} ${noun} match these filters`;
    return `${count} bot ${noun}`;
  }, [loading, totalCount, effectiveView, groupTotals, hasActiveFilters]);

  // Founder 2026-10-20: the DISCOVERY / TRANSACTIONAL mix pill that
  // used to render in the header was removed — the Sites list + site
  // switcher carry each site's real mode now, and the per-record kind
  // is already visible on each row. Keeping the mixSummary useMemo
  // around served no other reader.

  // ADR-0051 §2.2 — render the deletion date once at the banner site
  // so a long timestamp doesn't repeat inside the descriptive copy.
  // The site's `deleted_at` is an ISO-8601 UTC string; format to a
  // calendar date (YYYY-MM-DD) for the banner since the precise time
  // of deletion is not load-bearing for the archive view.
  const deletedAtDate = isDeletedSite && activeSite?.deleted_at !== undefined
    ? activeSite.deleted_at.slice(0, 10)
    : null;

  // The detail panel (desktop column and mobile drawer). In the grouped
  // view an agent row passes its group: header counts and the Confirmed
  // mark cover the whole range, and Activity lists the row's records.
  // ADR-0243 — a person's timeline (IPs only there, per the role rules).
  const renderPerson = (person_id: string): JSX.Element | null => (activeSite ? (
    <PersonTimeline
      key={person_id}
      siteId={activeSite.site_id}
      personId={person_id}
      siteDomain={activeSite.domain ?? null}
      onOpenRecord={(record_id) => {
        void (async () => {
          const rec = await api.mandates.get(record_id as AuditRecordId).catch(() => null);
          if (rec === null) return;
          openRecord(rec as MandateRecord, person_id);
        })();
      }}
      onForgotten={() => {
        setSelectedPersonId(null);
        setDrawerOpen(false);
        setPeopleReload((n) => n + 1);
      }}
    />
  ) : null);

  const renderDetail = (sel: MandateRecord): JSX.Element => (
    <>
      {returnPersonId !== null && returnGroup === undefined ? (
        <button
          type="button"
          onClick={() => {
            const pid = returnPersonId;
            setReturnPersonId(null);
            setSelected(null);
            setUrlRecordId(null);
            setSelectedPersonId(pid);
          }}
          data-testid="back-to-person"
          className="flex items-center gap-1 mb-2"
          style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted,
          }}
        >
          <ChevronLeft size={12} /> BACK TO VISITOR
        </button>
      ) : null}
      {returnGroup !== undefined ? (
        <button
          type="button"
          onClick={() => { setReturnGroupKey(null); openGroup(returnGroup); }}
          data-testid="back-to-group"
          className="flex items-center gap-1 mb-2"
          style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted,
          }}
        >
          <ChevronLeft size={12} /> {visitGroupName(returnGroup)}
        </button>
      ) : null}
      <MandateDetailV3
        containerLayout="flat"
        buyerReveal="auto"
        record={{
          ...sel,
          ...(selectedSettlement !== undefined ? { settlement: selectedSettlement } : {}),
          ...(selectedOriginalOrder !== undefined ? { original_order: selectedOriginalOrder } : {}),
          ...(selectedPageHits !== undefined ? { page_hits: selectedPageHits } : {}),
          ...(selectedAiSummary !== undefined ? { ai_summary: selectedAiSummary } : {}),
          ...(selectedAgentKey !== undefined ? { agent_key: selectedAgentKey } : {}),
          ...(selectedBotActionDismissed !== undefined ? { bot_action_dismissed: selectedBotActionDismissed } : {}),
          ...(selectedConnectorCall !== undefined ? { connector_call: selectedConnectorCall } : {}),
          ...(selectedOrderBreakdown !== undefined ? { order_breakdown: selectedOrderBreakdown } : {}),
          ...(selectedStaffTest !== undefined ? { staff_test: selectedStaffTest } : {}),
          ...(selectedLinkedOrder !== undefined ? { linked_order: selectedLinkedOrder } : {}),
        }}
        {...(selectedPayload !== undefined ? { payload: selectedPayload } : {})}
        {...(activeSite?.domain !== undefined ? { siteDomain: activeSite.domain } : {})}
        setView={setView}
        onOpenAttackers={(threat_ref) => {
          const opts: Parameters<SetView>[1] = { security_section: 'attackers' };
          if (threat_ref !== null && threat_ref.length > 0) opts.threat = threat_ref;
          setView('security', opts);
        }}
        onViewBotProfile={(agentKey, displayName) => {
          // The agent filter is a per-record filter: show every visit.
          if (effectiveView === 'agents') { setListView('visits'); writeListView('visits'); setAdvOpen(true); }
          setSelectedGroupKey(null);
          setReturnGroupKey(null);
          setAgentKeyFilter(agentKey); setQuery(displayName); setSelected(null); setDrawerOpen(false); setPage(1);
        }}
        onSearchAddress={(address) => {
          // Founder 2026-10-19: a scanner address opens every visit it made
          // (the server matches an IP in the search box).
          if (effectiveView === 'agents') { setListView('visits'); writeListView('visits'); }
          setSegment('bots');
          setSelectedGroupKey(null);
          setReturnGroupKey(null);
          setAgentKeyFilter(undefined); setQuery(address); setSelected(null); setDrawerOpen(false); setPage(1);
        }}
        {...(effectiveView === 'agents' && selectedGroup !== undefined && activeSite
          ? {
              group: selectedGroup,
              groupWindow: groupWindowLabel(dateFrom, dateTo),
              groupActivity: (
                <VisitGroupActivity
                  key={`${selectedGroup.group_key}|${JSON.stringify(groupQuery)}`}
                  siteId={activeSite.site_id}
                  group={selectedGroup}
                  query={groupQuery}
                  setView={setView}
                  onOpenRecord={(rec) => {
                    setReturnGroupKey(selectedGroup.group_key);
                    setReturnPersonId(null);
                    setSelectedGroupKey(null);
                    setSelected(rec);
                    setUrlRecordId(rec.record_id);
                  }}
                />
              ),
            }
          : {})}
      />
    </>
  );

  return (
    <VisitTrustProvider value={trustValue}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <NumLabel n={1}>Activity · {headerSubtitle}</NumLabel>
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(36px, 6vw, 52px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
            }}
          >
            Every visit and order, <em>on the record</em>.
          </h1>
        </div>
        {/* Founder 2026-10-20: the floating DISCOVERY / TRANSACTIONAL
            pill was redundant — the site switcher and the Sites list
            now carry each site's real mode. Dropped here to keep the
            page header clean. */}
      </div>

      {/* ADR-0263 §7.2 (S8) — plan-usage banner for the active site.
          Owner/admin only (role-gated inside). The near-state shows
          for orders (amber); exceeded states (page_loads, orders,
          assistant) show as coral. Hidden otherwise. */}
      {activeSite && (
        <div className="mb-4">
          <PlanUsageBanner site_id={activeSite.site_id} domain={activeSite.domain} />
        </div>
      )}

      {/* ADR-0051 §2.2 — DELETED SITE banner. Persistent (non-
          dismissable) so the merchant cannot mistake a read-only
          archive for an active site. Sky tone, mono kicker, matching
          the existing diagnostic banner pattern. Bundle export remains
          enabled via per-record actions further down; the embed
          snippet for this domain is no longer recognized (the soft-
          delete dropped the KV entry). */}
      {isDeletedSite && activeSite ? (
        <div
          className="p-4 mb-6"
          style={{
            background: C.skyDim,
            border: `1px solid rgba(127,184,255,0.3)`,
            borderRadius: 3,
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={12} color={C.skyFg} />
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.skyFg,
                letterSpacing: 1,
                fontWeight: 600,
              }}
            >
              DELETED-SITE READ-ONLY ARCHIVE
            </span>
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              lineHeight: 1.55,
            }}
          >
            {activeSite.domain} was removed
            {deletedAtDate !== null ? <> on {deletedAtDate}</> : null}
            . Past records remain stored on Sill&apos;s servers and
            exportable from this view. The embed snippet on this domain
            is no longer recognized — no new records will land here.
          </div>
        </div>
      ) : null}

      {/* Row 1 — full-width smooth search input + verification pills. */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div
          className="flex items-center gap-2 px-3 py-2 flex-1 min-w-[200px]"
          style={{
            background: searchFocused ? C.surface2 : C.surface,
            border: `1px solid ${searchFocused ? C.limeFg : C.borderSoft}`,
            borderRadius: 6,
            transition: 'border-color 120ms ease-out, background-color 120ms ease-out',
          }}
        >
          <Search size={14} color={searchFocused ? C.limeFg : C.muted} />
          <input
            value={query}
            onChange={(e) => changeQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search agent, page, user agent or IP"
            className="flex-1"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              padding: 0,
            }}
            aria-label="Search audit records"
          />
          {/* Founder 2026-10-19: a search takes a second; show it working. */}
          {query.length > 0 && (loading || (segment === 'suspicious' && effectiveView === 'agents' && sendersQuery !== query)) ? (
            <span
              role="status"
              aria-label="Searching"
              data-testid="search-loading"
              className="sill-spin"
              style={{ width: 12, height: 12, borderWidth: 1.5, flexShrink: 0 }}
            />
          ) : null}
          {query.length > 0 ? (
            <button
              type="button"
              onClick={() => changeQuery('')}
              aria-label="Clear search"
              className="flex items-center justify-center"
              style={{
                width: 18,
                height: 18,
                color: C.muted,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <X size={12} />
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {SEGMENT_CHIPS.filter((f) => f.id !== 'orders' || canTakePayments || segment === 'orders' || segmentCounts?.orders !== 0).map((f) => {
            const sel = segment === f.id;
            const suspicious = f.id === 'suspicious';
            // Founder 2026-10-20 — Suspicious moved to Security · Attackers.
            // The chip is kept in the row for continuity but now navigates
            // out of Activity instead of filtering it. `segment=suspicious`
            // still resolves internally for the record-level list and the
            // `?threat=` deep link path (both continue to work for callers
            // that rely on them).
            if (suspicious) {
              return (
                <button
                  type="button"
                  key={f.id}
                  onClick={() => setView('security', { security_section: 'attackers' })}
                  className="px-3 py-1.5"
                  data-segment-filter="suspicious"
                  data-testid="activity-suspicious-link"
                  aria-label="Suspicious traffic is in Security"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: 1,
                    color: C.coralFg,
                    background: 'transparent',
                    border: `1px solid ${C.coralFg}`,
                    borderRadius: 2,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  SUSPICIOUS <span aria-hidden="true">→</span>
                </button>
              );
            }
            return (
              <button
                type="button"
                key={f.id}
                onClick={() => changeSegment(f.id)}
                className="px-3 py-1.5"
                aria-pressed={sel}
                data-segment-filter={f.id}
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  letterSpacing: 1,
                  color: sel ? C.text : C.muted,
                  background: sel ? C.surface2 : 'transparent',
                  border: `1px solid ${sel ? C.border : 'transparent'}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {f.label.toUpperCase()}
              </button>
            );
          })}
        </div>
        {/* "More filters" toggle — expands Row 2 (status + date). Default
            collapsed unless the page mounted with active advanced filters
            (then auto-expanded). Surfaces an active-count badge so the
            merchant sees at a glance "I have filters" without scrolling. */}
        <button
          type="button"
          onClick={() => setAdvOpen((v) => !v)}
          className="flex items-center gap-1.5 px-2 py-1.5"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1,
            color: advOpen ? C.text : C.muted,
            background: 'transparent',
            border: '1px solid transparent',
            borderRadius: 2,
            cursor: 'pointer',
          }}
          aria-expanded={advOpen}
          aria-controls="mandates-adv-filters"
        >
          {advOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          MORE FILTERS
          {advActiveCount > 0 ? (
            <span style={{ color: C.limeFg, marginLeft: 4 }}>· {advActiveCount} ACTIVE</span>
          ) : null}
        </button>
      </div>

      {/* Transactions only (filters cleanup 2026-10-15): the outcome of each
          signed mandate. Every other chip lists observed visits only. */}
      {segment === 'orders' && !canTakePayments ? (
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="transaction-status-filters">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              flexShrink: 0,
            }}
          >
            STATUS
          </span>
          {renderStatusPills(changeStatus, status)}
        </div>
      ) : null}

      {/* Row 2 — status pills + date range + (when any filter is active) Clear.
          Collapsed by default per UX review 2026-06-01; defaults open when
          the page is loaded with active advanced filters. */}
      {advOpen ? (
      <div id="mandates-adv-filters" className="flex items-center gap-3 mb-5 flex-wrap">
        {canTakePayments ? (
          <div className="flex items-center gap-2 flex-wrap" data-testid="transaction-status-filters">
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 2,
                flexShrink: 0,
              }}
            >
              ORDERS
            </span>
            {renderStatusPills(pickOrderStatus, segment === 'orders' ? status : null)}
          </div>
        ) : null}
        {/* Identity (Step C: moved here from the main row). */}
        <div className="flex items-center gap-2 flex-wrap">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              flexShrink: 0,
            }}
          >
            IDENTITY
          </span>
          {IDENTITY_SEGMENTS.map((f) => (
            <button
              type="button"
              key={f.id}
              onClick={() => changeFilter(f.id)}
              className="px-3 py-1.5"
              aria-pressed={filter === f.id}
              data-identity-filter={f.id}
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1,
                color: filter === f.id ? C.text : C.muted,
                background: filter === f.id ? C.surface2 : 'transparent',
                border: `1px solid ${filter === f.id ? C.border : 'transparent'}`,
                borderRadius: 2,
                cursor: 'pointer',
              }}
            >
              {f.label.toUpperCase()}
            </button>
          ))}
        </div>
        {/* ADR-0253 §A — protocol filter chip. Reads the DERIVED protocol
            server-side so legacy rows (NULL column) filter right. */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="protocol-filters">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              flexShrink: 0,
            }}
          >
            PROTOCOL
          </span>
          {(['all', ...INBOUND_PROTOCOLS] as readonly string[]).map((p) => {
            const label = p === 'all'
              ? 'ALL'
              : (INBOUND_PROTOCOL_LABEL[p as keyof typeof INBOUND_PROTOCOL_LABEL] ?? p.toUpperCase());
            const active = protocol === p;
            return (
              <button
                type="button"
                key={p}
                onClick={() => changeProtocol(p)}
                className="px-3 py-1.5"
                aria-pressed={active}
                data-protocol-filter={p}
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  letterSpacing: 1,
                  color: active ? C.text : C.muted,
                  background: active ? C.surface2 : 'transparent',
                  border: `1px solid ${active ? C.border : 'transparent'}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                {label.toUpperCase()}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              flexShrink: 0,
            }}
          >
            DATE
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => changeDateFrom(e.target.value)}
            aria-label="From date"
            // Cap at today (or the existing `to` if earlier) so the native
            // picker won't let the merchant select a future date — future
            // audit records cannot exist, so future-from always returns
            // an empty page with no useful signal.
            max={dateTo.length > 0 ? dateTo : today}
            className="px-2 py-1.5"
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: dateFrom.length > 0 ? C.text : C.muted,
              background: C.surface,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 2,
              outline: 'none',
              colorScheme: 'dark',
            }}
          />
          <span style={{ fontFamily: monoFont, fontSize: 9, color: C.dim }}>→</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => changeDateTo(e.target.value)}
            aria-label="To date"
            // `to` cannot be in the future and cannot be earlier than `from`.
            // `undefined` over empty-string for `min` to avoid the Safari quirk.
            {...(dateFrom.length > 0 ? { min: dateFrom } : {})}
            max={today}
            className="px-2 py-1.5"
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: dateTo.length > 0 ? C.text : C.muted,
              background: C.surface,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 2,
              outline: 'none',
              colorScheme: 'dark',
            }}
          />
        </div>
        {/* Audit log view (ADR-0246; was "Show every visit" in the main
            row): the per-record list for audits and exports, one signed
            entry per visitor per hour. A view, not a filter: it changes how
            the list is laid out. Persisted as ?list=visits. Hidden when the
            API has no grouped view (the list is per-record anyway). */}
        {!groupsUnavailable ? (
          <Tooltip content={AUDIT_LOG_VIEW_TOOLTIP} side="bottom" data-testid="audit-log-view-tip">
          <button
            type="button"
            onClick={() => changeListView(listView === 'visits' ? 'agents' : 'visits')}
            className="px-2 py-1.5"
            aria-pressed={listView === 'visits'}
            data-testid="audit-log-view"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1,
              color: listView === 'visits' ? C.text : C.muted,
              background: listView === 'visits' ? C.surface2 : 'transparent',
              border: `1px solid ${listView === 'visits' ? C.border : 'transparent'}`,
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            AUDIT LOG VIEW
          </button>
          </Tooltip>
        ) : null}
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-1 px-2 py-1.5"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1,
              color: C.coralFg,
              background: 'transparent',
              border: `1px solid ${C.coralFg}`,
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            <X size={10} /> CLEAR
          </button>
        ) : null}
      </div>
      ) : null}

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <Card>
            {loadError && (effectiveView === 'agents' ? groups.length === 0 && (people === null || people.length === 0) : records.length === 0) ? (
              <div data-testid="visits-load-error">
                <EmptyStateCard
                  icon={ShieldAlert}
                  label="Couldn't load this view"
                  hint="Sill will try again in a few seconds. If it keeps failing, narrow the date range."
                />
              </div>
            ) : (effectiveView === 'agents'
                   ? groups.length === 0 && (people === null || people.length === 0)
                     && !(segment === 'suspicious' && senders !== null && senders.length === 0 && query.trim().length > 0)
                     // Address and campaign cards are list rows too: never
                     // hide them behind "No matches" (founder 2026-10-18,
                     // mobile showed an empty Suspicious list).
                     && !(segment === 'suspicious'
                       && ((senders !== null && senders.length > 0) || (campaigns !== null && campaigns.length > 0)))
                   : records.length === 0)
                && !loading ? (
              emptyStaleSignal !== null ? (
                <EmptyStateCard
                  icon={ShieldAlert}
                  label="No recent records"
                  hint={
                    <>
                      No beacons from this site in {emptyStaleSignal.age_label}.
                      The site key may have rotated out of sync with your
                      deployed snippet — records resume once the install key
                      matches again.
                      <button
                        type="button"
                        onClick={() => setView('settings')}
                        style={{
                          display: 'block',
                          margin: '10px auto 0',
                          fontFamily: sansFont,
                          fontSize: 12,
                          color: C.limeFg,
                          fontWeight: 500,
                          textDecoration: 'underline',
                          background: 'transparent',
                          border: 0,
                          padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        Re-check the snippet in Settings →
                      </button>
                    </>
                  }
                />
              ) : hasActiveFilters ? (
                <EmptyStateCard
                  label="No matches"
                  hint="No audit records match these filters. Widen the range or clear the filters to see all traffic."
                />
              ) : (
                <EmptyStateCard
                  label="No records"
                  hint="No agent traffic recorded yet. Records appear here as soon as the embed sees its first visit."
                />
              )
            ) : protocol === 'ucp' && activeSite ? (
              /* Founder 2026-10-23 — UCP checkout lifecycle view (unsigned).
                 Replaces the per-agent groups when the protocol chip is UCP;
                 the right panel shows the session timeline + the signed
                 record link once the checkout completes. */
              <UcpSessionsList
                siteId={activeSite.site_id}
                selectedSessionId={selectedUcpSession}
                onSelect={(session) => {
                  setSelectedUcpSession(session.session_id);
                  setSelected(null);
                  setSelectedGroupKey(null);
                  setSelectedPersonId(null);
                  setDrawerOpen(true);
                }}
              />
            ) : (
              effectiveView === 'agents' ? (
                <>
                {/* Suspicious v2 (2026-10-17) — one card per sender IP.
                    The attacking senders live under an ATTACKS kicker;
                    the existing Unknown-scanner / probing rows keep
                    working below under OTHER SUSPICIOUS VISITS. */}
                {/* Empty-search feedback (founder 2026-10-18): when the
                    merchant's query filters the sender list to zero, say
                    so inline — a muted single line keeps the Sill voice
                    (no exclamation marks, no shouty empty-state card).
                    Shown even when visitors / earlier sessions below have
                    rows, so it is clear WHICH list came up empty. */}
                {segment === 'suspicious' && senders !== null && senders.length === 0 && query.trim().length > 0 ? (
                  <div
                    data-testid="suspicious-empty-search"
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.muted,
                      padding: '14px 16px',
                    }}
                  >
                    No suspicious addresses match &ldquo;{query.trim()}&rdquo;.
                  </div>
                ) : null}
                {/* ADR-0248 S2 — probe-campaign cards above the sender list.
                    One card per detected campaign; opens the detail panel
                    on click. Hidden when none fit the window. */}
                {segment === 'suspicious' && campaigns !== null && campaigns.length > 0 ? (
                  <div
                    data-testid="suspicious-campaigns"
                    style={{ padding: '14px 16px 4px 16px' }}
                  >
                    <div
                      data-testid="suspicious-campaigns-kicker"
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 9,
                        letterSpacing: 2,
                        color: C.muted,
                        marginBottom: 8,
                      }}
                    >
                      CAMPAIGNS &middot; {campaigns.length}
                    </div>
                    {campaigns.map((c) => (
                      <CampaignCard
                        key={c.campaign_id}
                        campaign={c}
                        isSelected={selectedCampaign?.campaign_id === c.campaign_id}
                        onSelect={() => { openCampaign(c); setDrawerOpen(true); }}
                      />
                    ))}
                  </div>
                ) : null}
                {segment === 'suspicious' && senders !== null && senders.length > 0 ? (
                  <>
                    <div
                      data-testid="suspicious-attacks-kicker"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 9,
                        letterSpacing: 2,
                        color: C.muted,
                        padding: '10px 16px 6px 16px',
                      }}
                    >
                      <span>
                        ATTACKS &middot; {senders.length} SENDER{senders.length === 1 ? '' : 'S'}
                      </span>
                      {/* Founder bug 2026-10-18: small link to the block
                          list (Security › Blocking since 2026-10-20). Hidden when there are 0 blocks AND
                          blocking is off (nothing useful to see). */}
                      {suspiciousBlocking !== null
                        && (suspiciousBlocking.live_blocks > 0 || suspiciousBlocking.mode === 'on') ? (
                        <button
                          type="button"
                          data-testid="suspicious-blocked-link"
                          onClick={() => {
                            try {
                              const url = new URL(window.location.href);
                              url.searchParams.set('view', 'security');
                              url.searchParams.set('section', 'blocking');
                              url.searchParams.delete('settings_tab');
                              window.history.pushState({}, '', url.toString());
                              window.dispatchEvent(new PopStateEvent('popstate'));
                            } catch { /* best effort */ }
                          }}
                          style={{
                            marginLeft: 'auto',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            color: C.limeFg,
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 9,
                            letterSpacing: 2,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          BLOCKED &middot; {suspiciousBlocking.live_blocks} &rarr;
                        </button>
                      ) : null}
                    </div>
                    {senders.map((s, i) => (
                      <SenderCard
                        key={s.sender_ref}
                        sender={s}
                        isSelected={selectedSender?.sender_ref === s.sender_ref}
                        isLast={
                          i === senders.length - 1
                          && visibleGroups.length === 0
                          && (people === null || people.length === 0)
                        }
                        onSelect={() => { openSender(s); setDrawerOpen(true); }}
                        campaignId={campaignBySender[s.sender_ref] ?? null}
                        onSelectCampaign={(campaign_id) => {
                          const match = campaigns?.find((c) => c.campaign_id === campaign_id);
                          if (match !== undefined) {
                            openCampaign(match);
                            setDrawerOpen(true);
                          }
                        }}
                      />
                    ))}
                    {visibleGroups.length > 0 || (people !== null && people.length > 0) ? (
                      <div
                        data-testid="suspicious-other-kicker"
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 9,
                          letterSpacing: 2,
                          color: C.muted,
                          padding: '14px 16px 6px 16px',
                          borderTop: `1px solid ${C.borderSoft}`,
                        }}
                      >
                        OTHER SUSPICIOUS VISITS
                      </div>
                    ) : null}
                  </>
                ) : null}
                {showPeopleCards && peopleHiddenForSupport ? (
                  <div
                    data-testid="people-hidden-support"
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.muted,
                      padding: '12px 16px',
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    People are hidden during a Sill support session to protect this site's visitors. The account owner sees them when signed in.
                  </div>
                ) : null}
                {people !== null ? people.map((p, i) => (
                  <PersonRow
                    key={p.person_id}
                    person={p}
                    isSelected={selectedPersonId === p.person_id}
                    isLast={i === people.length - 1 && visibleGroups.length === 0}
                    onSelect={() => openPerson(p)}
                  />
                )) : null}
                {visibleGroups.map((g, i) => (
                  <VisitGroupRow
                    key={g.group_key}
                    group={g}
                    setView={setView}
                    {...(people !== null && g.group_key === 'people' ? { peopleLabel: 'Earlier visitors' } : {})}
                    isSelected={selectedGroupKey === g.group_key
                      || (selectedGroupKey === null && g.kind === 'record' && selected?.record_id === g.representative.record_id)}
                    isLast={i === visibleGroups.length - 1}
                    onSelect={() => {
                      pinnedRecordRef.current = null;
                      setReturnGroupKey(null);
                      if (g.kind === 'record') {
                        openRecord(g.representative as MandateRecord);
                      } else {
                        openGroup(g);
                      }
                      setDrawerOpen(true);
                    }}
                  />
                ))}
                </>
              ) : visibleRecords.map((r, i) => {
                const serverAgentId = r.identification_match?.agent_id;
                return (
                  <VisitRecordRow
                    key={r.record_id}
                    record={r}
                    isSelected={selected?.record_id === r.record_id}
                    isNew={newRecordIds.has(r.record_id)}
                    isFlash={r.record_id === flashRecordId}
                    isLast={i >= records.length - 1}
                    agentStats={serverAgentId !== undefined ? agentStats.get(serverAgentId) : undefined}
                    onSelect={() => {
                      // Explicit click supersedes the deep-link pin and any
                      // open person or group (founder bug 1).
                      openRecord(r);
                    }}
                  />
                );
              })
            )}
          </Card>
          {pagerTotalPages > 1 ? (
            <Pager
              C={C}
              page={page}
              totalPages={pagerTotalPages}
              totalCount={peoplePager ? peoplePaging.total : totalCount}
              pageSize={MANDATES_PAGE_SIZE}
              rowsOnPage={peoplePager ? (people?.length ?? 0) : effectiveView === 'agents' ? groups.length : records.length}
              onGoTo={goToPage}
            />
          ) : null}
        </div>
        {/* DESKTOP detail: sticky side panel, hidden on mobile (the Drawer
            below owns the phone layout). Sticky done right — it lives on the
            COLUMN with `self-start` so the column shrinks to its content
            instead of stretching to the flex row's height (a stretched child
            has no room to pin); it pins at `top-24` and scrolls its own
            content when the detail runs long. `flat` MandateDetail so the
            card doesn't re-apply its own sticky/overflow.

            Scroll-to-top on selection change (founder feedback 2026-10-17):
            clicking a threat row after scrolling a long agent detail used
            to leave the pane scrolled DOWN, which read as "the previous
            agent detail is still visible underneath the threat". `data-
            selection-key` rebinds to the current selection and a small
            effect below resets scrollTop whenever it changes. */}
        <div
          ref={detailPaneRef}
          data-selection-key={selectedCampaign !== null
            ? `c:${selectedCampaign.campaign_id}`
            : selectedSender !== null
              ? `s:${selectedSender.sender_ref}`
              : selectedPersonId !== null
                ? `p:${selectedPersonId}`
                : selected !== null
                  ? `r:${selected.record_id}`
                  : 'empty'}
          className="hidden lg:block lg:w-[640px] lg:flex-shrink-0 lg:self-start lg:sticky lg:top-24 lg:max-h-[calc(100vh_-_7rem)] lg:overflow-y-auto"
        >
          {!isMobile
            ? (selectedCampaign !== null && activeSite
                ? (
                  <CampaignDetail
                    siteId={activeSite.site_id}
                    siteDomain={activeSite.domain ?? ''}
                    campaign={selectedCampaign}
                    onChanged={() => setSendersReload((n) => n + 1)}
                    onSelectMember={(sender_ref) => {
                      const match = senders?.find((s) => s.sender_ref === sender_ref);
                      if (match !== undefined) {
                        openSender(match);
                        setDrawerOpen(true);
                      }
                    }}
                  />
                )
                : selectedSender !== null && activeSite
                  ? <SenderDetail siteId={activeSite.site_id} siteDomain={activeSite.domain ?? null} sender={selectedSender} onChanged={() => setSendersReload((n) => n + 1)} />
                  : selectedUcpSession !== null && activeSite && protocol === 'ucp'
                    ? (
                        <UcpSessionDetail
                          siteId={activeSite.site_id}
                          sessionId={selectedUcpSession}
                          onOpenAuditRecord={(record_id) => {
                            void (async () => {
                              const rec = await api.mandates.get(record_id as AuditRecordId).catch(() => null);
                              if (rec === null) return;
                              setSelectedUcpSession(null);
                              setSelected(rec as MandateRecord);
                              setUrlRecordId(rec.record_id);
                            })();
                          }}
                        />
                      )
                    : selectedPersonId !== null
                      ? renderPerson(selectedPersonId)
                      : selected
                        ? renderDetail(selected)
                        : null)
            : null}
        </div>
      </div>

      {/* MOBILE detail: full-screen panel that REPLACES the list (mail-app
          pattern). Slides in from the right on row tap; the ← BACK button
          returns to the list. No scrim / backdrop — the panel is solid so
          the list is invisible behind it, giving a true replace feel.
          Only ONE panel mounts per breakpoint — desktop column gates on
          `!isMobile`, this panel on `isMobile` — so there is no double-fetch. */}
      <AnimatePresence>
        {isMobile && drawerOpen && (selected || selectedPersonId !== null || selectedSender !== null || selectedCampaign !== null) ? (
          <motion.div
            key="mobile-detail"
            data-testid="mobile-detail-panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.22, ease: [0.32, 0, 0.67, 0] }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              background: C.bg,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
            onTouchStart={onSwipeTouchStart}
            onTouchEnd={onSwipeTouchEnd}
          >
            {/* Sticky back bar + prev/next */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 16px',
                borderBottom: `1px solid ${C.border}`,
                position: 'sticky',
                top: 0,
                background: C.bg,
                zIndex: 1,
              }}
            >
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Back to list"
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '4px 0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: monoFont,
                  fontSize: 10,
                  letterSpacing: 1.5,
                  color: C.muted,
                }}
              >
                <ChevronLeft size={14} />
                BACK
              </button>
              {/* Prev / position / Next */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <button
                  type="button"
                  onClick={goToPrev}
                  disabled={!canGoPrev}
                  aria-label="Previous mandate"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '6px 8px',
                    cursor: canGoPrev ? 'pointer' : 'default',
                    color: canGoPrev ? C.muted : C.dim,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 9,
                    color: C.dim,
                    letterSpacing: 1,
                    minWidth: 40,
                    textAlign: 'center',
                  }}
                >
                  {selectedIndex >= 0 ? `${selectedIndex + 1} / ${navLength}` : ''}
                </span>
                <button
                  type="button"
                  onClick={goToNext}
                  disabled={!canGoNext}
                  aria-label="Next mandate"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '6px 8px',
                    cursor: canGoNext ? 'pointer' : 'default',
                    color: canGoNext ? C.muted : C.dim,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
            {/* Panel content */}
            <div className="p-4">
              {selectedCampaign !== null && activeSite
                ? (
                    <CampaignDetail
                      siteId={activeSite.site_id}
                      siteDomain={activeSite.domain ?? ''}
                      campaign={selectedCampaign}
                      onChanged={() => setSendersReload((n) => n + 1)}
                      onSelectMember={(sender_ref) => {
                        const match = senders?.find((s) => s.sender_ref === sender_ref);
                        if (match !== undefined) openSender(match);
                      }}
                    />
                  )
                : selectedSender !== null && activeSite
                  ? <SenderDetail
                      siteId={activeSite.site_id}
                      siteDomain={activeSite.domain ?? null}
                      sender={selectedSender}
                      onChanged={() => setSendersReload((n) => n + 1)}
                    />
                  : selectedPersonId !== null
                    ? renderPerson(selectedPersonId)
                    : selected
                      ? renderDetail(selected)
                      : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
    </VisitTrustProvider>
  );
}

/**
 * Compute a compact, windowed list of page numbers for the pager:
 * always the first and last page, plus the current page and its
 * immediate neighbours, with `'gap'` markers standing in for the
 * collapsed ranges. e.g. page 7 of 20 → [1, gap, 6, 7, 8, gap, 20].
 */
function pageWindow(page: number, total: number): Array<number | 'gap'> {
  const around = new Set<number>([1, total]);
  for (let p = page - 1; p <= page + 1; p += 1) {
    if (p >= 1 && p <= total) around.add(p);
  }
  const sorted = [...around].sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  let prev = 0;
  for (const n of sorted) {
    if (prev !== 0 && n - prev > 1) out.push('gap');
    out.push(n);
    prev = n;
  }
  return out;
}

interface PagerProps {
  C: ReturnType<typeof useC>;
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  rowsOnPage: number;
  onGoTo: (p: number) => void;
}

/**
 * Numbered pager — "showing X–Y of N" on the left, Prev / windowed page
 * numbers / Next plus a jump-to-page box on the right. Rendered only
 * when there is more than one page (the parent gates on `totalPages`).
 */
function Pager({
  C,
  page,
  totalPages,
  totalCount,
  pageSize,
  rowsOnPage,
  onGoTo,
}: PagerProps): JSX.Element {
  const [jump, setJump] = useState('');
  const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = (page - 1) * pageSize + rowsOnPage;
  const windowed = pageWindow(page, totalPages);

  const submitJump = (): void => {
    const n = Number(jump);
    if (Number.isInteger(n) && n >= 1) onGoTo(n);
    setJump('');
  };

  const navBtn = (
    enabled: boolean,
    onClick: () => void,
    label: string,
    icon: JSX.Element,
  ): JSX.Element => (
    <button
      type="button"
      disabled={!enabled}
      onClick={onClick}
      aria-label={label}
      className="flex items-center justify-center"
      style={{
        width: 26,
        height: 26,
        background: 'transparent',
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        color: enabled ? C.text : C.dim,
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.5,
      }}
    >
      {icon}
    </button>
  );

  return (
    <div className="flex items-center justify-between gap-3 py-4 flex-wrap">
      <span
        style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted }}
      >
        SHOWING {start.toLocaleString('en-US')}–{end.toLocaleString('en-US')} OF {totalCount.toLocaleString('en-US')}
      </span>
      <div className="flex items-center gap-1 flex-wrap">
        {navBtn(page > 1, () => onGoTo(page - 1), 'Previous page', <ChevronLeft size={13} />)}
        {windowed.map((w, i) =>
          w === 'gap' ? (
            <span
              key={`gap-${i}`}
              style={{ fontFamily: monoFont, fontSize: 11, color: C.dim, padding: '0 2px' }}
            >
              …
            </span>
          ) : (
            <button
              type="button"
              key={w}
              onClick={() => onGoTo(w)}
              aria-label={`Page ${w}`}
              aria-current={w === page ? 'page' : undefined}
              className="flex items-center justify-center px-2"
              style={{
                minWidth: 26,
                height: 26,
                fontFamily: monoFont,
                fontSize: 11,
                background: w === page ? C.surface2 : 'transparent',
                border: `1px solid ${w === page ? C.limeFg : C.borderSoft}`,
                color: w === page ? C.text : C.muted,
                borderRadius: 2,
                cursor: 'pointer',
              }}
            >
              {w}
            </button>
          ),
        )}
        {navBtn(page < totalPages, () => onGoTo(page + 1), 'Next page', <ChevronRight size={13} />)}
        <input
          value={jump}
          onChange={(e) => setJump(e.target.value.replace(/[^\d]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitJump();
          }}
          onBlur={() => jump.length > 0 && submitJump()}
          inputMode="numeric"
          aria-label="Jump to page"
          placeholder="Go to"
          className="px-2"
          style={{
            width: 56,
            height: 26,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.text,
            background: C.surface,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
            outline: 'none',
          }}
        />
      </div>
    </div>
  );
}
